import { FrontendExtensionProps } from '../../../common/ExtensionWrapper/interfaces';
import {
  useEffect,
  useState,
  Dispatch,
  SetStateAction,
  useRef,
  useMemo,
} from 'react';
import {
  AvailableExternalQueries,
  TipoRol,
} from '../../../common/ExtensionWrapper/query-signs';
import { IReceiptHeader, IReceiptDetail } from '../../../../interfaces/Receipt';
import {
  Button,
  Alert,
  Divider,
  Input,
  Select,
  Spin,
  Steps,
  Tooltip,
  Tag,
  Form,
  Table,
  Modal,
  FormInstance,
  InputNumber,
} from 'antd';
import './FinnegansAnexoYDimensiones.less';
import { MinusCircleOutlined, PlusCircleOutlined } from '@ant-design/icons';
import ButtonAction from '../../../common/Buttons/ButtonAction';
import ButtonPrimary from '../../../common/Buttons/ButtonPrimary';
import useReceiptService from '../../../../hooks/useReceiptService';
import ButtonSecondary from '../../../common/Buttons/ButtonSecondary';
import { formatValueOnLoad } from '../../../../shared/formatValueOnLoad';
import { ITenantSettingCurrentValue } from '../../../../interfaces/TenantSetting';
import { EnumsValues } from '../../../../enums/EnumsValues';
import GraphqlService from '../../../../services/graphql/GraphqlService';

const TIPO_CONDICION_CONTADO = 1;
const DESCRIPTION_MATCH_THRESHOLD = 0.5; // Umbral mínimo de similitud de descripción para vincular items (0..1)
const WORKFLOW_SUBCONTRATISTA = 'ConComSub';
const WORKFLOW_INSUMOS = 'CPRA-INS';
const WORKFLOW_SERCON = 'CPRA-SERCON';

enum PaymentMethodEnum {
  contado = 'Contado',
  cuentaCorriente = 'Cuenta Corriente',
}

enum PaymentType {
  EFECTIVO = 'efectivo',
  BANCO = 'banco',
  OTRO = 'otro',
}

enum DebeHaber {
  DEBE = 1,
  HABER = -1,
}

enum Moneda {
  PESOS = 'PES',
  DOLARES = 'DOL',
}

enum DocumentoCategoriaId {
  ORDEN_DE_COMPRA = -3,
  RECIBO_FLETES = -2, // Recepciones (?)
  CERTIFICADO_SUBCONTRATISTA = -606,
  CERTIFICADO_AVANCE_OBRA = -604,
}

enum OperacionBancariaModo {
  NO_USA_DOCUMENT0 = 0,
  USA_DOCUMENTO_EXISTENTE = 1,
  USA_DOCUMENTO_EXISTENTE_PARCIALMENTE = 5,
  ALTA_DOCUMENTO_DE_TERCERO = 2,
  ALTA_DOCUMENTO_PROPIO = 3,
  USA_CONTRAPARTIDA = 4,
  ALTA_DOCUMENTO_DE_TERCERO_O_EFECTIVO = 6,
}

interface BranchItem {
  codigo: string;
  nombre: string;
  activo: boolean;
}

interface ExtraData {
  providerCode?: string;
  originDocument?: string;
  products?: ExtensionProduct[];
  paymentMethod: PaymentMethodEnum;
  payOrderPayments?: PayOrderPayments[];
  paymentTerm?: string;
  workflow?: string;
  originDocumentType?: number;
  originReceptions?: string[];
  originReceptionsType?: number;
  originCertificates?: string[];
  originCertificatesType?: number;
  isAdvanceInvoiceFlow?: boolean;
  useCertItemsInPayload?: boolean;
  anticipoProductCode?: string;
  anticipoAmount?: number;
  companyBranchCode?: string;
  copyDimensionsToOtherTaxes?: boolean;
  description?: string;
}

interface ExtensionProduct {
  code: string;
  dimensions: ExtensionDimension[];
  detailId: number;
  description: string;
  originDocument: string;
}

interface ExtensionDimension {
  name: string;
  code: string;
  distribution: string;
  allowedIncompleteDistribution?: number | null;
  values: ExtensionDimensionValue[];
}

interface ExtensionDimensionValue {
  name?: string;
  code: string;
  percentage: number;
}

interface RelatedDocument {
  OPERACIONITEMID: number;
  ORGANIZACION: string;
  DOCUMENTO: string;
  COMPROBANTE: string;
  FECHA: string;
  PRODUCTO: string;
  PRODUCTOCODIGO: string;
  CICLOITEMID: string | null;
  BPMPROCESOACTIVIDADID: string | null;
  PENDIENTE: number;
  UNIDAD: string | null;
  PRECIO: number;
  MONEDA: string;
  PARTIDA: string | null;
  DESCRIPCION: string | null;
  FECHAHASTA: string | null;
  FECHAPROXIMOPASO: string | null;
  '@@CLASEVO': string;
  TRANSACCIONID: number;
  IDENTIFICACIONEXTERNA: string;
  SUCURSAL: string;
  TRANSACCIONCATEGORIAID: number;
  NOMBRE: string;
  DIMENSIONDISTRIBUCION?: FinnegansDimension[];
}

interface ExtensionRelatedDocument {
  name: string;
  code: string;
  receipt?: string;
  certificate?: string;
  products: ExtensionRelatedDocumentProduct[];
}

interface ExtensionRelatedDocumentProduct {
  code: string;
  quantity: number;
  price?: number;
  dimensions?: FinnegansDimension[];
}

interface Dimension {
  DIMENSIONCODIGO: string;
  DIMENSIONNOMBRE: string;
  DIMENSIONDISTRIBUCIONCODIGO?: string | null;
  DIMENSIONDISTRIBUCIONNOMBRE?: string | null;
  PERMITEDISTRIBUCIONINCOMPLETA?: number | null;
}

interface DimensionValue {
  CODIGO: string;
  NOMBRE: string;
  ACTIVO: boolean;
  ID: number;
}

interface FinnegansOrdenCompraResponse {
  IdentificacionExterna: string;
  Nombre: string;
  Items: OrdenCompraItem[];
  ProveedorCodigo?: string;
  WorkflowCodigo?: string;
  PorcentajeAnticipoFinanciero?: number;
  ProductoAnticipoFinancieroCodigo?: string;
}

interface FinnegansReceptionsResponse {
  IdentificacionExterna: string;
  Nombre: string;
  Items: ReceptionItem[];
}

interface FinnegansCertificatesResponse {
  IdentificacionExterna: string;
  Nombre: string;
  Items: CertificateItem[];
  WorkflowCodigo?: string;
  PorcentajeAnticipoFinanciero?: number;
  ProductoAnticipoFinancieroCodigo?: string;
}

interface OrdenCompraItem {
  ProductoCodigo: string;
  ImporteExento: number;
  Cantidad: number;
  vinculacionOrigen: string | null;
  CantidadPendiente: number;
  Descripcion: string | null;
  DimensionDistribucion: FinnegansDimension[];
  Precio: number;
}

interface ReceptionItem {
  ProductoCodigo: string;
  Cantidad: number;
  vinculacionOrigen: string | null;
  Precio?: number;
}

interface CertificateItem {
  ProductoCodigo: string;
  Cantidad: number;
  vinculacionOrigen: string | null;
  vinculacionDestino?: string | null;
  Precio?: number;
  Descripcion?: string | null;
  DimensionDistribucion: FinnegansDimension[];
  IdentificacionExterna?: string;
}

interface FinnegansDimension {
  tipoCalculo: string;
  distribucionCodigo?: string;
  distribucionItems: DistribucionItem[];
  dimensionCodigo: string;
}

interface DistribucionItem {
  codigo: string;
  porcentaje: number;
}

interface FinnegansAccountDimension {
  DimensionCodigo: string;
  DimensionDistribucionCodigo: string | null;
  DimensionValoresCodigo: string | null;
  PermiteDistribucionIncompleta: boolean;
  Tipo: number;
}

interface FinnegansAccountResponse {
  Codigo: string;
  Nombre: string;
  CuentaDimension: FinnegansAccountDimension[];
}

interface DistributionResponse {
  dimensionDistribucionCodigo: string;
  dimensionDistribucionNombre: string;
  valores: DistributionValueResponse[];
}

interface DistributionValueResponse {
  dimensionDistribucionValorId: number;
  porcentaje: number;
}

interface Distribution {
  dimensionDistributionCode: string;
  dimensionDistributionName: string;
  values: ExtensionDimensionValue[];
}

interface OperacionesBancarias {
  CODIGO: string;
  NOMBRE: string;
  DESCRIPCION: string;
  UTILIZACHEQUERA: boolean;
  CUENTACODIGODEFAULT: string;
  CUENTANOMBREDEFAULT: string;
  MODO: number;
}

interface CashPaymentMethod {
  type: PaymentType.EFECTIVO;
  DebeHaber: -1;
  ImporteMonTransaccion: number;
  MonedaCodigo: string;
  CuentaCodigo: string;
  DimensionDistribucion?: FinnegansDimension[];
}

interface BankPaymentMethod {
  type: PaymentType.BANCO;
  OperacionBancariaCodigo: string;
  CuentaCodigo: string;
  DebeHaber: -1;
  ImporteMonTransaccion: number;
  MonedaCodigo: string;
  NumeroDocumentoFisico: string;
  DimensionDistribucion?: FinnegansDimension[];
}

interface OtherPaymentMethod {
  type: PaymentType.OTRO;
  DebeHaber: -1 | 1;
  ImporteMonTransaccion: number;
  MonedaCodigo: string;
  CuentaCodigo: string;
  DimensionDistribucion?: FinnegansDimension[];
}

interface AccountValue {
  CODIGO: string;
  NOMBRE: string;
  USO: 'Banco' | 'Efectivo' | 'Otro';
}

interface EfectivoResponse {
  CuentaCodigo: string;
  CuentaID: string;
  DimensionDistribucion: {
    dimensionCodigo: string;
    distribucionItems: { codigo: string; id?: string; porcentaje: string }[];
  }[];
}

interface WebhookReceiptDetail {
  receipt_line_number: number;
  amount: number;
  DimensionDistribucion: {
    dimensionCodigo: string;
    distribucionItems: { codigo: string; porcentaje: string }[];
  }[];
}

export interface PayConditionItemsResponse {
  Fecha: string | null;
  Tipo: number;
  Dias: number;
  Porcentaje: number;
}
export interface FinnegansPaymentTerm {
  Codigo: string;
  Activo: boolean;
  Nombre: string;
  Tipo: number;
  EdicionFija: boolean;
  ExigeDocumentosDiferidos: boolean;
  PorcentajeInteres: number | null;
  CtaProveedores: string | null;
  CtaDisponibilidad: string | null;
  //TODO:
  // ETA/ETD:;
  CondicionPagoItems: PayConditionItemsResponse[] | null;
}

interface PaymentTerm {
  code: string;
  name: string;
  default: boolean;
}

interface FinnegansPaymentCondition {
  CondicionPagoCodigo: string;
  Default: boolean;
}

interface FinnegansProvider {
  Nombre: string;
  Codigo: string;
  Activo: boolean;
  RestriccionCondPagos: boolean;
  CondicionesPago: FinnegansPaymentCondition[];
}

type PayOrderPayments =
  | CashPaymentMethod
  | BankPaymentMethod
  | OtherPaymentMethod;

const DISTRIBUTION_MANUAL = 'manual';

const getWorkflowFromCertificates = (
  certificates: FinnegansCertificatesResponse[],
): string | undefined => {
  const workflows = Array.from(
    new Set(certificates.map((c) => c.WorkflowCodigo).filter(Boolean)),
  );
  return workflows.length === 1 ? (workflows[0] as string) : undefined;
};

const validateFormByProduct = (product: ExtensionProduct) => {
  return product.dimensions.every((dimension: ExtensionDimension) => {
    if (
      dimension.allowedIncompleteDistribution === EnumsValues.ConstNumbers.one
    ) {
      return true;
    }
    if (
      dimension.values.length > 0 &&
      dimension.values.every((value) => value.percentage > 0 && value.code)
    ) {
      const sum = dimension.values.reduce((acc, value) => {
        acc = acc + value.percentage;
        return acc;
      }, 0);
      return sum === 100;
    }
    return false;
  });
};

export function FinnegansAnexoYDimensiones(props: FrontendExtensionProps) {
  const [error, setError] = useState<any>();
  const [currentStep, setCurrentStep] = useState(0);
  const [dimensions, setDimensions] = useState<Record<string, Dimension[]>>();
  const [dimensionValues, setDimensionValues] =
    useState<Record<string, DimensionValue[]>>();
  const [receiptHeader, setReceiptHeader] = useState<IReceiptHeader>();
  const [availableDocuments, setAvailableDocuments] = useState<
    ExtensionRelatedDocument[]
  >([]);
  const [documentRelatedData, setDocumentRelatedData] = useState<
    FinnegansOrdenCompraResponse[]
  >([]);
  const [receptionRelatedData, setReceptionRelatedData] = useState<
    FinnegansReceptionsResponse[]
  >([]);
  const [certificateRelatedData, setCertificateRelatedData] = useState<
    FinnegansCertificatesResponse[]
  >([]);
  const [certAmountWarning, setCertAmountWarning] =
    useState<string | null>(null);
  const [isValidForm, setIsValidForm] = useState<boolean>(false);
  const [extraData, setExtraData] = useState<ExtraData>();
  const [distributions, setDistributions] =
    useState<Record<string, Distribution[]>>();
  const [accounts, setAccounts] = useState<AccountValue[]>([]);
  const [bankOperations, setBankOperations] = useState<OperacionesBancarias[]>(
    [],
  );
  const [hasDifferenceInPayOrder, setHasDifferenceInPayOrder] =
    useState<boolean>(false);
  const [paymentTerms, setPaymentTerms] = useState<PaymentTerm[]>([]);
  const [branchList, setBranchList] = useState<BranchItem[]>([]);

  const changeCounter = useRef<number>(0);
  const lastSavedChangeCounter = useRef<number>(0);
  const hasInitialized = useRef<boolean>(false);

  const currentProduct = extraData?.products?.[currentStep];
  const currentProductIsValid =
    currentProduct && validateFormByProduct(currentProduct);

  /* Card de error */
  const [showStepValidateMessage, setShowStepValidateMessage] = useState(false);

  const { Query, customRequest } = GraphqlService();

  const [dataSettingInputs, setDataSettingInputs] =
    useState<ITenantSettingCurrentValue | null>(null);

  const [dataSettingLabor, setDataSettingLabor] =
    useState<ITenantSettingCurrentValue | null>(null);

  const [dataSettingCertificate, setDataSettingCertificate] =
    useState<ITenantSettingCurrentValue | null>(null);

  const [dataSettingAdvanceInvoice, setDataSettingAdvanceInvoice] =
    useState<ITenantSettingCurrentValue | null>(null);

  const [availableReceptions, setAvailableReceptions] = useState<
    ExtensionRelatedDocument[]
  >([]);

  const [availableCertificates, setAvailableCertificates] = useState<
    ExtensionRelatedDocument[]
  >([]);

  /* Si el producto actual, pasa a validar, oculto el mensaje */
  useEffect(() => {
    setShowStepValidateMessage(false);
  }, [currentProductIsValid]);

  useEffect(() => {
    changeCounter.current++;
  }, [extraData]);

  const saveUnfinishedExtraData = () => {
    if (extraData && changeCounter.current !== lastSavedChangeCounter.current) {
      saveExtensionData();
      lastSavedChangeCounter.current = changeCounter.current;
    }
  };

  /* Guarda cuando se cambie de paso o cuando se carga un plazo de pago */
  useEffect(() => {
    saveUnfinishedExtraData();
  }, [currentStep, extraData?.paymentTerm]);

  /* Guarda cuando el producto pasa a ser valido */
  useEffect(() => {
    if (currentProductIsValid) saveUnfinishedExtraData();
  }, [currentProductIsValid, extraData]);

  const { getReceiptHeaderWithProduct } = useReceiptService();

  useEffect(() => {
    /* Intencional para que los errores puedan ser capturados por el Error Boundary */
    if (error) {
      throw new Error(error);
    }
  }, [error]);

  /* Guarda cuando hay cambios en array de cancelaciones */
  useEffect(() => {
    if (extraData && extraData.payOrderPayments) {
      saveExtensionData();
    }
  }, [extraData?.payOrderPayments]);

  const getDimensionValuesForProductByOC = (
    response: FinnegansOrdenCompraResponse,
  ) => {
    const dimensionValuesForProduct: Record<string, {}[]> = {};
    response.Items?.forEach((item: OrdenCompraItem) => {
      const product = item.ProductoCodigo;
      item.DimensionDistribucion?.forEach((dimension: FinnegansDimension) => {
        if (Array.isArray(dimension.distribucionItems)) {
          const distribution = dimension.distribucionItems.map(
            (distributionItem) => ({
              code: distributionItem.codigo,
              percentage: distributionItem.porcentaje,
            }),
          );

          const dimensionData = {
            code: dimension.dimensionCodigo,
            distribution: dimension.distribucionCodigo,
            values: distribution,
          };

          if (!dimensionValuesForProduct[product]) {
            dimensionValuesForProduct[product] = [];
          }
          dimensionValuesForProduct[product].push(dimensionData);
        }
      });
    });

    return dimensionValuesForProduct;
  };

  // ── Helper: flujo OC (settings + filtrado + fetch OC data + filtro anticipo) ──

  const fetchOcFlowData = async (
    tenantId: number,
    initialGroupedDocuments: ExtensionRelatedDocument[],
    receiptHeaderData: any,
  ): Promise<{
    buyOrderData: FinnegansOrdenCompraResponse[];
    filteredGroupedDocuments: ExtensionRelatedDocument[];
    advanceInvoiceSettingResult: ITenantSettingCurrentValue | null;
  }> => {
    const exactOcSetting: ITenantSettingCurrentValue = await customRequest({
      query: Query.tenantSettingCurrentValue,
      variables: {
        tenant_id: tenantId,
        name: EnumsValues.TenantSettingNames.AllowOnlyExactOC,
      },
    });
    const allowOnlyExactOC =
      typeof exactOcSetting?.value === 'string' &&
      exactOcSetting.value.toLowerCase() === 'true';

    const advanceSetting: ITenantSettingCurrentValue = await customRequest({
      query: Query.tenantSettingCurrentValue,
      variables: {
        tenant_id: tenantId,
        name: EnumsValues.TenantSettingNames.EnableAdvanceInvoiceFlow,
      },
    });
    const isAdvanceInvoiceEnabled =
      advanceSetting?.value?.toLowerCase() === 'true';

    // 2.3 Filtrar OCs válidas
    const filtered = initialGroupedDocuments.filter(
      (OC: ExtensionRelatedDocument) => {
        if (allowOnlyExactOC) {
          /* comportamiento limitado por requerimiento del Hinojo:
           solo se puede usar una OC si los productos y cantidades coinciden exactamente */
          return OC.products.every((product: ExtensionRelatedDocumentProduct) =>
            receiptHeaderData.receipt_detail.some(
              (detail: IReceiptDetail) =>
                detail.product!.code === product.code &&
                detail.quantity === product.quantity,
            ),
          );
        }
        const hasAtLeastOneProductInReceipt = OC.products.some(
          (product: ExtensionRelatedDocumentProduct) =>
            receiptHeaderData.receipt_detail?.some(
              (detail: IReceiptDetail) => detail.product!.code === product.code,
            ),
        );
        const allProductsInReceiptAreValid = OC.products.every((product) => {
          const productInReceipt = receiptHeaderData.receipt_detail?.find(
            (detail: IReceiptDetail) => detail.product!.code === product.code,
          );
          if (!productInReceipt || !productInReceipt.quantity) return true;
          return productInReceipt.quantity <= product.quantity;
        });
        return hasAtLeastOneProductInReceipt && allProductsInReceiptAreValid;
      },
    );

    // 2.4 Obtener datos completos de las OCs
    const buyOrderData = await props.queryExternalService<
      FinnegansOrdenCompraResponse[],
      AvailableExternalQueries.GET_BUY_ORDERS
    >({
      query: AvailableExternalQueries.GET_BUY_ORDERS,
      inputData: {
        buyOrderCodes: filtered.map(
          (doc: ExtensionRelatedDocument) => doc.code,
        ),
      },
    });

    // 2.4 Si el flujo de anticipo está habilitado, filtrar por producto anticipo y porcentaje
    if (isAdvanceInvoiceEnabled) {
      const ADVANCE_TOLERANCE = 1.0;
      const invoiceTotal = receiptHeaderData.receipt_detail.reduce(
        (sum: number, detail: IReceiptDetail) =>
          sum + (detail.quantity || 0) * (detail.unit_price || 0),
        0,
      );
      const invoiceProductCodes = new Set(
        receiptHeaderData.receipt_detail.map(
          (d: IReceiptDetail) => d.product?.code,
        ),
      );
      const validAdvanceOcCodes = new Set(
        buyOrderData
          .filter((oc) => {
            if (
              !oc.ProductoAnticipoFinancieroCodigo ||
              oc.PorcentajeAnticipoFinanciero == null
            )
              return false;
            const ocItemsTotal = oc.Items.reduce(
              (sum, item) => sum + (item.Cantidad || 0) * (item.Precio || 0),
              0,
            );
            const expectedAnticipo =
              (oc.PorcentajeAnticipoFinanciero / 100) * ocItemsTotal;
            return (
              invoiceProductCodes.has(oc.ProductoAnticipoFinancieroCodigo) &&
              Math.abs(invoiceTotal - expectedAnticipo) < ADVANCE_TOLERANCE
            );
          })
          .map((oc) => oc.IdentificacionExterna),
      );

      return {
        buyOrderData: buyOrderData.filter((oc) =>
          validAdvanceOcCodes.has(oc.IdentificacionExterna),
        ),
        filteredGroupedDocuments: filtered.filter((doc) =>
          validAdvanceOcCodes.has(doc.code),
        ),
        advanceInvoiceSettingResult: advanceSetting,
      };
    }

    return {
      buyOrderData,
      filteredGroupedDocuments: filtered,
      advanceInvoiceSettingResult: advanceSetting,
    };
  };

  // ── Helper: dimensiones y distribuciones ──────────────────────────────────────

  const fetchDimensionsAndDistributions = async (
    uniqueProductCodes: Set<string>,
    paymentMethod: string,
  ): Promise<{
    dimensionsByProductCode: Record<string, Dimension[]>;
    dimensionValuesByDimensionCode: Record<string, DimensionValue[]>;
    parsedDistributionValues: Record<string, Distribution[]>;
    productsWithDimensions: string[];
  }> => {
    const dimensionsByProductCode = await props.queryExternalService<
      Record<string, Dimension[]>,
      AvailableExternalQueries.GET_DIMENSIONS
    >({
      query: AvailableExternalQueries.GET_DIMENSIONS,
      inputData: {
        productCodes: Array.from(uniqueProductCodes),
        paymentMethod,
      },
    });

    const uniqueDimensionCodes = new Set<string>();
    Object.keys(dimensionsByProductCode).forEach((productCode) => {
      dimensionsByProductCode[
        productCode as keyof typeof dimensionsByProductCode
      ].forEach((dimension: Dimension) =>
        uniqueDimensionCodes.add(dimension.DIMENSIONCODIGO),
      );
    });

    const productsWithDimensions = Array.from(uniqueProductCodes).filter(
      (productCode) =>
        dimensionsByProductCode[
          productCode as keyof typeof dimensionsByProductCode
        ]?.length > 0,
    );

    const dimensionValuesByDimensionCode = await props.queryExternalService<
      Record<string, DimensionValue[]>,
      AvailableExternalQueries.GET_DIMENSION_VALUES
    >({
      query: AvailableExternalQueries.GET_DIMENSION_VALUES,
      inputData: { dimensionCodes: Array.from(uniqueDimensionCodes) },
    });

    const distributionValuesByDimensionCode: Record<
      string,
      DistributionResponse[]
    > = await props.queryExternalService<
      Record<string, DistributionResponse[]>,
      AvailableExternalQueries.GET_DISTRIBUTIONS
    >({
      query: AvailableExternalQueries.GET_DISTRIBUTIONS,
      inputData: { dimensionCodes: Array.from(uniqueDimensionCodes) },
    });

    const parsedDistributionValues = Object.entries(
      distributionValuesByDimensionCode,
    ).reduce(
      (
        acc: Record<string, Distribution[]>,
        [dimensionName, distributions]: [string, DistributionResponse[]],
      ) => {
        acc[dimensionName] = distributions.map(
          (distribution: DistributionResponse): Distribution => ({
            dimensionDistributionCode: distribution.dimensionDistribucionCodigo,
            dimensionDistributionName: distribution.dimensionDistribucionNombre,
            values: distribution.valores.map(
              (valor: DistributionValueResponse) => {
                const dimensionValue = dimensionValuesByDimensionCode[
                  dimensionName
                ].find(
                  (value) => value.ID === valor.dimensionDistribucionValorId,
                );
                return {
                  code: dimensionValue?.CODIGO || '',
                  name: dimensionValue?.NOMBRE || '',
                  percentage: valor.porcentaje,
                };
              },
            ),
          }),
        );
        return acc;
      },
      {},
    );

    return {
      dimensionsByProductCode,
      dimensionValuesByDimensionCode,
      parsedDistributionValues,
      productsWithDimensions,
    };
  };

  // ── Helper: datos de pago (cuentas, operaciones bancarias y términos) ─────────

  const fetchPaymentData = async (
    paymentMethod: PaymentMethodEnum,
    providerCode: string,
  ): Promise<{
    accountsData: AccountValue[];
    filteredBankOperations: OperacionesBancarias[];
    normalizedPaymentTerms: PaymentTerm[];
  }> => {
    let accountsData: AccountValue[] = [];
    let bankOperationsData: OperacionesBancarias[] = [];

    if (paymentMethod === PaymentMethodEnum.contado) {
      accountsData = await props.queryExternalService<
        any,
        AvailableExternalQueries.GET_PAY_ORDER_ACCOUNTS
      >({
        query: AvailableExternalQueries.GET_PAY_ORDER_ACCOUNTS,
        inputData: {},
      });
      bankOperationsData = await props.queryExternalService<
        any,
        AvailableExternalQueries.GET_BANK_OPERATION
      >({ query: AvailableExternalQueries.GET_BANK_OPERATION, inputData: {} });
    }

    // En esta instancia, no utilizamos valores que exijan chequera
    const filteredBankOperations = bankOperationsData.filter(
      (operation) =>
        (!operation.UTILIZACHEQUERA &&
          operation.MODO === OperacionBancariaModo.NO_USA_DOCUMENT0) ||
        operation.MODO === OperacionBancariaModo.ALTA_DOCUMENTO_PROPIO,
    );

    const provider = await props.queryExternalService<
      FinnegansProvider,
      AvailableExternalQueries.GET_PROVIDER
    >({
      query: AvailableExternalQueries.GET_PROVIDER,
      inputData: { providerCode },
    });

    const paymentTermsData = await props.queryExternalService<
      FinnegansPaymentTerm[],
      AvailableExternalQueries.GET_PAYMENT_TERMS
    >({ query: AvailableExternalQueries.GET_PAYMENT_TERMS, inputData: {} });

    const filteredPaymentTerms = paymentTermsData.filter((term) => {
      if (!term.Activo) return false;
      return paymentMethod === PaymentMethodEnum.contado
        ? term.Tipo === TIPO_CONDICION_CONTADO
        : true;
    });

    const validatedProviderPaymentTerms = provider.CondicionesPago.filter(
      (term) =>
        filteredPaymentTerms.some(
          (pt) => pt.Codigo === term.CondicionPagoCodigo,
        ),
    );

    const normalizedPaymentTerms: PaymentTerm[] = [];
    if (provider.RestriccionCondPagos) {
      // 8.3 Solo mostrar los plazos definidos en el proveedor
      normalizedPaymentTerms.push(
        ...validatedProviderPaymentTerms.map(
          (pt: FinnegansPaymentCondition) => ({
            code: pt.CondicionPagoCodigo,
            name:
              filteredPaymentTerms.find(
                (term) => term.Codigo === pt.CondicionPagoCodigo,
              )?.Nombre || '',
            default: pt.Default || false,
          }),
        ),
      );
    } else {
      // 8.4 Mostrar todos pero preseleccionar los definidos en el proveedor
      normalizedPaymentTerms.push(
        ...filteredPaymentTerms.map((term) => ({
          code: term.Codigo,
          name: term.Nombre,
          default:
            validatedProviderPaymentTerms.find(
              (pt) => pt.CondicionPagoCodigo === term.Codigo,
            )?.Default || false,
        })),
      );
    }

    return { accountsData, filteredBankOperations, normalizedPaymentTerms };
  };

  // ── Helper: settings de flujo (inputs, labor, certificados) ──────────────────

  const fetchFlowSettings = async (
    tenantId: number,
  ): Promise<{
    dataSettingInputs: ITenantSettingCurrentValue;
    dataSettingLabor: ITenantSettingCurrentValue;
    dataSettingCertificate: ITenantSettingCurrentValue;
  }> => {
    const [dataSettingInputs, dataSettingLabor, dataSettingCertificate] =
      await Promise.all([
        customRequest({
          query: Query.tenantSettingCurrentValue,
          variables: {
            tenant_id: tenantId,
            name: EnumsValues.TenantSettingNames.EnableInputsFlow,
          },
        }),
        customRequest({
          query: Query.tenantSettingCurrentValue,
          variables: {
            tenant_id: tenantId,
            name: EnumsValues.TenantSettingNames.EnableLaborFlow,
          },
        }),
        customRequest({
          query: Query.tenantSettingCurrentValue,
          variables: {
            tenant_id: tenantId,
            name: EnumsValues.TenantSettingNames.EnableCertificateFlow,
          },
        }),
      ]);
    return { dataSettingInputs, dataSettingLabor, dataSettingCertificate };
  };

  // ── Helper: recepciones (filtrado, agrupación y validación) ──────────────────

  const fetchReceptions = async (
    availableDocuments: RelatedDocument[],
    receiptHeaderData: any,
  ): Promise<{
    receptionsData: FinnegansReceptionsResponse[];
    validReceptions: ExtensionRelatedDocument[];
  }> => {
    const filteredReceptions = availableDocuments.filter(
      (doc) =>
        doc.TRANSACCIONCATEGORIAID === DocumentoCategoriaId.RECIBO_FLETES,
    );

    // 9.1 Filtrar por número de remito si existe
    let filteredByNumber = filteredReceptions;
    let hasRemitoNumberMatch = false;

    if (receiptHeaderData.related_document_number) {
      const relatedDocNumber = receiptHeaderData.related_document_number;

      const extractRemitoNumbers = (docNumber: string): string[] => {
        // Caso: "00001-00001104" -> toma los últimos 8 dígitos
        if (docNumber.includes('-')) {
          const parts = docNumber.split('-');
          if (parts.length === 2 && parts[1].length >= 8)
            return [parts[1].slice(-8)];
        }
        // Caso: "32833 - 32834" -> separar y normalizar a 8 dígitos
        const numbers = docNumber.split(/[-\s]+/).filter((n) => n.trim());
        return numbers.map((num) => {
          const cleanNum = num.trim();
          return /^\d+$/.test(cleanNum) ? cleanNum.padStart(8, '0') : cleanNum;
        });
      };

      const remitoNumbersToMatch = extractRemitoNumbers(relatedDocNumber);
      if (remitoNumbersToMatch.length > 0) {
        const matched = filteredReceptions.filter((doc) => {
          const receiptNumber = doc.COMPROBANTE || '';
          const extractNumberFromReceipt = (receipt: string) =>
            receipt.length >= 8 ? receipt.slice(-8) : receipt.padStart(8, '0');
          const docNumber = extractNumberFromReceipt(receiptNumber);
          return remitoNumbersToMatch.some(
            (remitoNum) => docNumber === remitoNum.padStart(8, '0'),
          );
        });
        if (matched.length > 0) {
          filteredByNumber = matched;
          hasRemitoNumberMatch = true;
        }
      }
    }

    // 9.2 Agrupar por IDENTIFICACIONEXTERNA
    const groupedReceptions: ExtensionRelatedDocument[] =
      filteredByNumber.reduce(
        (acc: ExtensionRelatedDocument[], doc: RelatedDocument) => {
          const key = doc.IDENTIFICACIONEXTERNA;
          let group = acc.find((item) => item.code === key);
          if (!group) {
            group = {
              code: key,
              name: doc.DOCUMENTO,
              receipt: doc.COMPROBANTE,
              products: [
                {
                  code: doc.PRODUCTOCODIGO,
                  quantity: doc.PENDIENTE,
                  price: doc.PRECIO,
                },
              ],
            };
            acc.push(group);
          } else {
            group.products.push({
              code: doc.PRODUCTOCODIGO,
              quantity: doc.PENDIENTE,
              price: doc.PRECIO,
            });
          }
          return acc;
        },
        [],
      );

    // 9.3 Filtro de productos solo si no hay coincidencia por número de remito
    const validReceptions = hasRemitoNumberMatch
      ? groupedReceptions
      : groupedReceptions.filter((rec: ExtensionRelatedDocument) =>
          rec.products.some((product) =>
            receiptHeaderData.receipt_detail?.some(
              (detail: IReceiptDetail) => detail.product?.code === product.code,
            ),
          ),
        );

    const receptionsData = validReceptions.map(
      (rec): FinnegansReceptionsResponse => ({
        IdentificacionExterna: rec.code,
        Nombre: rec.name,
        Items: rec.products.map((p) => ({
          ProductoCodigo: p.code,
          Cantidad: p.quantity,
          vinculacionOrigen: null,
          Precio: p.price,
        })),
      }),
    );

    return { receptionsData, validReceptions };
  };

  // ── Helper: certificados (filtrado, agrupación, validación y fetch) ───────────

  const fetchCertificates = async (
    availableDocuments: RelatedDocument[],
    receiptHeaderData: any,
  ): Promise<{
    certificatesData: FinnegansCertificatesResponse[];
    validCertificates: ExtensionRelatedDocument[];
  }> => {
    const allowedCertificateTypes = [
      DocumentoCategoriaId.CERTIFICADO_SUBCONTRATISTA,
      DocumentoCategoriaId.RECIBO_FLETES,
      DocumentoCategoriaId.CERTIFICADO_AVANCE_OBRA,
    ];

    // 10.2 Agrupar certificados por IDENTIFICACIONEXTERNA
    const groupedCertificates: ExtensionRelatedDocument[] = availableDocuments
      .filter((doc) =>
        allowedCertificateTypes.includes(doc.TRANSACCIONCATEGORIAID),
      )
      .reduce((acc: ExtensionRelatedDocument[], doc: RelatedDocument) => {
        const key = doc.IDENTIFICACIONEXTERNA;
        let group = acc.find((item) => item.code === key);
        if (!group) {
          group = {
            code: key,
            name: doc.DOCUMENTO,
            receipt: doc.COMPROBANTE,
            products: [
              {
                code: doc.PRODUCTOCODIGO,
                quantity: doc.PENDIENTE,
                price: doc.PRECIO,
              },
            ],
          };
          acc.push(group);
        } else {
          group.products.push({
            code: doc.PRODUCTOCODIGO,
            quantity: doc.PENDIENTE,
            price: doc.PRECIO,
          });
        }
        return acc;
      }, []);

    // 10.3 Filtro inicial (coarse): el certificado es válido si cumple CASO 1, CASO 2 o potencialmente CASO 3.
    //   CASO 1: al menos un item del cert tiene un total de línea (qty×precio) que coincide 1:1
    //           con una línea de factura del mismo producto, con unicidad en ambos lados.
    //   CASO 2: todos los items del cert son del mismo producto y la suma de sus importes
    //           coincide con el total de la línea de factura de ese producto.
    //   CASO 3 (potencial): igual a CASO 2 pero cert supera la factura (se valida con anticipo en paso fino).
    const invoiceDetails: IReceiptDetail[] =
      receiptHeaderData.receipt_detail ?? [];
    const coarseValidCertificates = groupedCertificates.filter(
      (cer: ExtensionRelatedDocument) => {
        // CASO 1
        const hasCase1Match = cer.products.some((cp) => {
          const certLineTotal = (cp.quantity || 0) * (cp.price || 0);
          const invoiceSameProduct = invoiceDetails.filter(
            (d: IReceiptDetail) => d.product?.code === cp.code,
          );
          const invoiceMatches = invoiceSameProduct.filter(
            (d: IReceiptDetail) =>
              Math.abs(
                (d.quantity || 0) * (d.unit_price || 0) - certLineTotal,
              ) < 0.01,
          );
          if (invoiceMatches.length !== 1) return false;
          const invoiceLineTotal =
            (invoiceMatches[0].quantity || 0) *
            (invoiceMatches[0].unit_price || 0);
          const certSameTotal = cer.products.filter(
            (p) =>
              p.code === cp.code &&
              Math.abs((p.quantity || 0) * (p.price || 0) - invoiceLineTotal) <
                0.01,
          );
          return certSameTotal.length === 1;
        });
        if (hasCase1Match) return true;

        // CASO 2: todos los items del cert tienen el mismo ProductoCodigo y la suma de sus
        // importes coincide con el total de la línea de factura de ese producto.
        // CASO 3: igual a CASO 2 pero la suma del cert supera el total de factura
        //         (la diferencia será cubierta por un ítem de anticipo negativo).
        const certCodes = new Set(cer.products.map((p) => p.code));
        if (certCodes.size === 1) {
          const productCode = Array.from(certCodes)[0];
          const certSumTotal = cer.products.reduce(
            (sum, p) => sum + (p.quantity || 0) * (p.price || 0),
            0,
          );
          const invoiceLineForProduct = invoiceDetails.find(
            (d: IReceiptDetail) => d.product?.code === productCode,
          );
          if (invoiceLineForProduct) {
            const invoiceLineTotal =
              (invoiceLineForProduct.quantity || 0) *
              (invoiceLineForProduct.unit_price || 0);
            // CASO 2: suma exacta; CASO 3: suma mayor (el exceso lo cubre el anticipo)
            if (certSumTotal >= invoiceLineTotal - 0.01) return true;
          }
        }

        return false;
      },
    );

    // 10.4 Fetch de datos completos del certificado (necesario para validar anticipo en CASO 3)
    let certificatesData: FinnegansCertificatesResponse[] = [];
    if (coarseValidCertificates.length > 0) {
      certificatesData = await props.queryExternalService<
        FinnegansCertificatesResponse[],
        AvailableExternalQueries.GET_SUBCONTRACTOR_CERTIFICATES
      >({
        query: AvailableExternalQueries.GET_SUBCONTRACTOR_CERTIFICATES,
        inputData: {
          certificatesCodes: coarseValidCertificates.map((doc) => doc.code),
        },
      });
    }

    // 10.5 Filtro fino: si el cert supera el total de la factura (CASO 3), debe tener anticipo.
    //      certTotal - anticipoAmount debe ser ≈ invoiceTotal.
    const invoiceTotal = (
      (receiptHeaderData.receipt_detail as IReceiptDetail[]) ?? []
    ).reduce(
      (acc: number, d: IReceiptDetail) =>
        acc + (d.unit_price || 0) * (d.quantity || 0),
      0,
    );

    const validCertificates = coarseValidCertificates.filter((cer) => {
      const certSumTotal = cer.products.reduce(
        (sum, p) => sum + (p.quantity || 0) * (p.price || 0),
        0,
      );
      if (certSumTotal <= invoiceTotal + 0.01) return true; // CASO 1 o CASO 2: sin anticipo

      // CASO 3: cert supera la factura → validar que tiene anticipo financiero
      const fullCert = certificatesData.find(
        (c) => c.IdentificacionExterna === cer.code,
      );
      return (
        (fullCert?.PorcentajeAnticipoFinanciero ?? 0) > 0 &&
        !!fullCert?.ProductoAnticipoFinancieroCodigo
      );
    });

    return { certificatesData, validCertificates };
  };

  // ── Helper: autocompletado desde webhook (Efectivo + dimensiones) ─────────────

  const applyWebhookAutocomplete = async (
    formData: ExtraData,
    receiptHeaderData: any,
    accountsData: AccountValue[],
    dimensionValuesByDimensionCode: Record<string, DimensionValue[]>,
    parsedDistributionValues: Record<string, Distribution[]>,
  ): Promise<ExtraData> => {
    // if (formData.paymentMethod !== PaymentMethodEnum.contado) return formData;

    try {
      const dataSettingWebhookUrl: ITenantSettingCurrentValue =
        await customRequest({
          query: Query.tenantSettingCurrentValue,
          variables: {
            tenant_id: receiptHeaderData?.tenant_id,
            name: EnumsValues.TenantSettingNames.VendorByEmailWebhookUrl,
          },
        });

      const rawWebhookUrl = dataSettingWebhookUrl?.value;
      const webhookUrl = (() => {
        if (!rawWebhookUrl) return rawWebhookUrl;
        try {
          const parsed = JSON.parse(rawWebhookUrl);
          return Array.isArray(parsed) ? parsed[0] : rawWebhookUrl;
        } catch {
          return rawWebhookUrl;
        }
      })();

      if (!webhookUrl) return formData;
      formData.copyDimensionsToOtherTaxes = true; // Logica particular de DASER

      const vendorEmail = receiptHeaderData?.from_email ?? undefined;
      if (!vendorEmail) return formData;

      const vendorData = await props.queryExternalService<
        {
          data: {
            Efectivo?: EfectivoResponse;
            receipt_detail?: WebhookReceiptDetail[];
            sucursal_codigo?: string;
          };
        },
        AvailableExternalQueries.GET_VENDOR_DATA_BY_EMAIL
      >({
        query: AvailableExternalQueries.GET_VENDOR_DATA_BY_EMAIL,
        inputData: {
          payload: {
            data: {
              from_email: vendorEmail,
              receipt_detail: receiptHeaderData.receipt_detail ?? [],
            },
          },
          webhookURL: webhookUrl,
          clientId: receiptHeaderData.client_id,
          clientSecret: receiptHeaderData.client_secret,
          tenant_id: receiptHeaderData.tenant_id,
        },
      });

      const result = { ...formData };
      // 11.1 Autocompletar Efectivo (solo si no hay pagos guardados)
      const efectivo = vendorData?.data?.Efectivo;
      if (
        efectivo?.CuentaCodigo &&
        (!result.payOrderPayments || result.payOrderPayments.length === 0)
      ) {
        const cuentaValida = accountsData.some(
          (acc) =>
            acc.CODIGO === efectivo.CuentaCodigo && acc.USO === 'Efectivo',
        );
        if (cuentaValida) {
          const dimensionDistribucion: FinnegansDimension[] | undefined =
            Array.isArray(efectivo.DimensionDistribucion)
              ? efectivo.DimensionDistribucion.map((dim: any) => {
                  // Intentamos buscar la dimensión original para matchear case
                  const dKey = Object.keys(dimensionValuesByDimensionCode).find(
                    (k) =>
                      k.toLowerCase() === dim.dimensionCodigo?.toLowerCase(),
                  );
                  const exactDimCode = dKey || dim.dimensionCodigo;
                  const dValues =
                    dimensionValuesByDimensionCode[exactDimCode] || [];
                  return {
                    dimensionCodigo: exactDimCode,
                    tipoCalculo: '2',
                    distribucionItems: (dim.distribucionItems || []).map(
                      (item: any) => {
                        const exactVal = dValues.find(
                          (dv) =>
                            dv.CODIGO?.toLowerCase() ===
                            item.codigo?.toLowerCase(),
                        );
                        return {
                          codigo: exactVal ? exactVal.CODIGO! : item.codigo,
                          porcentaje: parseFloat(item.porcentaje),
                        };
                      },
                    ),
                  };
                })
              : undefined;

          result.payOrderPayments = [
            {
              type: PaymentType.EFECTIVO,
              DebeHaber: DebeHaber.HABER,
              ImporteMonTransaccion: receiptHeaderData.receipt_total ?? 0,
              MonedaCodigo: Moneda.PESOS,
              CuentaCodigo: efectivo.CuentaCodigo,
              DimensionDistribucion: dimensionDistribucion?.length
                ? dimensionDistribucion
                : undefined,
            } as CashPaymentMethod,
          ];
        }
      }

      // 11.2 Autocompletar dimensiones de productos desde receipt_detail del webhook
      const webhookDetail = vendorData?.data?.receipt_detail;
      if (Array.isArray(webhookDetail) && webhookDetail.length > 0) {
        const webhookDimMap = new Map(
          webhookDetail.map((d) => [
            d.receipt_line_number,
            d.DimensionDistribucion ?? [],
          ]),
        );

        result.products = result.products?.map((product: ExtensionProduct) => {
          const matchingDetail = receiptHeaderData.receipt_detail.find(
            (d: IReceiptDetail) => d.id === product.detailId,
          );
          if (!matchingDetail || matchingDetail.receipt_line_number == null)
            return product;

          const webhookDims = webhookDimMap.get(
            matchingDetail.receipt_line_number,
          );
          if (!webhookDims || webhookDims.length === 0) return product;

          return {
            ...product,
            dimensions: product.dimensions.map((dim: ExtensionDimension) => {
              const webhookDim = webhookDims.find(
                (wd) =>
                  wd.dimensionCodigo.toLowerCase() === dim.code.toLowerCase(),
              );
              if (!webhookDim) return dim;

              const dValues = dimensionValuesByDimensionCode[dim.code] || [];
              const mappedValues: ExtensionDimensionValue[] =
                webhookDim.distribucionItems.map((item) => {
                  const exactVal = dValues.find(
                    (dv) =>
                      dv.CODIGO?.toLowerCase() === item.codigo.toLowerCase(),
                  );
                  return {
                    code: exactVal ? exactVal.CODIGO! : item.codigo,
                    percentage: parseFloat(item.porcentaje),
                  };
                });

              // Mergear valores ya guardados con los del webhook por código y porcentaje
              const existingValues = dim.values || [];
              const mergedValues = [...existingValues];
              mappedValues.forEach((mVal) => {
                const exists = mergedValues.some(
                  (v) =>
                    v.code === mVal.code && v.percentage === mVal.percentage,
                );
                if (!exists) mergedValues.push(mVal);
              });

              const dimDistributions = parsedDistributionValues[dim.code] || [];
              let matchedDistributionCode = DISTRIBUTION_MANUAL;
              const isExactMatch = (dist: any) => {
                if (dist.values.length !== mergedValues.length) return false;
                return dist.values.every((dval: any) =>
                  mergedValues.some(
                    (mval: any) =>
                      mval.code === dval.code &&
                      mval.percentage === dval.percentage,
                  ),
                );
              };
              for (const d of dimDistributions) {
                if (isExactMatch(d)) {
                  matchedDistributionCode = d.dimensionDistributionCode;
                  break;
                }
              }

              return {
                ...dim,
                values: mergedValues,
                distribution: matchedDistributionCode,
              };
            }),
          };
        });
      }

      // 11.3 Autocompletar sucursal desde webhook (solo si no hay una ya guardada)
      if (vendorData?.data?.sucursal_codigo && !result.companyBranchCode) {
        result.companyBranchCode = vendorData.data.sucursal_codigo;
      }

      return result;
    } catch {
      // Silencioso: si falla el webhook, el usuario completa manualmente
      return formData;
    }
  };

  // ── Orquestador principal ─────────────────────────────────────────────────────────

  const initializeData = async () => {
    try {
      // 1. Validar datos básicos
      const receiptHeaderData = await getReceiptHeaderWithProduct(
        props.resourceId,
      ); // TODO: deberia ser un internalQuery

      if (!receiptHeaderData) {
        setError('Receipt header not found');
        return;
      }
      if (!receiptHeaderData.receipt_total) {
        setError('Receipt total not found');
        return;
      }
      if (
        !receiptHeaderData.receipt_detail ||
        !receiptHeaderData.receipt_detail.every(
          (detail: IReceiptDetail) => detail?.product?.code,
        )
      ) {
        setError('Receipt detail not found or product has not code');
        return;
      }

      const extraData: ExtraData =
        await props.getExtensionDocumentInformation();
      if (!extraData.providerCode) {
        setError(
          'Provider code not found, it should be available in this extension step',
        );
        return;
      }

      // 1.5 Obtener lista de sucursales
      const branchListData = await props.queryExternalService<
        BranchItem[],
        AvailableExternalQueries.GET_BRANCH_LIST
      >({
        query: AvailableExternalQueries.GET_BRANCH_LIST,
        inputData: {},
      });
      const activeBranches = (branchListData || []).filter((b) => b.activo);

      // 2. Obtener y agrupar documentos relacionados
      const availableDocuments: RelatedDocument[] =
        await props.queryExternalService<
          any,
          AvailableExternalQueries.GET_RELATED_DOCUMENTS
        >({
          query: AvailableExternalQueries.GET_RELATED_DOCUMENTS,
          inputData: {
            providerCode: extraData.providerCode,
            rolType: TipoRol.Destino,
            paymentMethod: extraData.paymentMethod,
            workflow:
              extraData.workflow && !isCertificate
                ? extraData.workflow
                : undefined,
          },
        });

      // TODO: solo contemplamos dos casos, si workflow esta presente asumimos que necesitamos fletes
      const filterCase = extraData.workflow
        ? DocumentoCategoriaId.RECIBO_FLETES
        : DocumentoCategoriaId.ORDEN_DE_COMPRA;

      let groupedDocuments: ExtensionRelatedDocument[] = availableDocuments
        .filter(
          (doc: RelatedDocument) => doc?.TRANSACCIONCATEGORIAID === filterCase,
        )
        .reduce((acc: ExtensionRelatedDocument[], doc: RelatedDocument) => {
          const key = doc.IDENTIFICACIONEXTERNA;
          let group = acc.find((item) => item.code === key);
          if (!group) {
            group = {
              code: key,
              name: doc.DOCUMENTO,
              products: [{ code: doc.PRODUCTOCODIGO, quantity: doc.PENDIENTE }],
            };
            acc.push(group);
          } else {
            group.products.push({
              code: doc.PRODUCTOCODIGO,
              quantity: doc.PENDIENTE,
            });
          }
          return acc;
        }, []);

      // 3. Flujo OC: filtrado + fetch datos completos + filtro anticipo (si aplica)
      let buyOrderData: FinnegansOrdenCompraResponse[] = [];
      let advanceInvoiceSettingResult: ITenantSettingCurrentValue | null = null;

      if (!extraData.workflow) {
        const ocResult = await fetchOcFlowData(
          receiptHeaderData.tenant_id,
          groupedDocuments,
          receiptHeaderData,
        );
        buyOrderData = ocResult.buyOrderData;
        groupedDocuments = ocResult.filteredGroupedDocuments;
        advanceInvoiceSettingResult = ocResult.advanceInvoiceSettingResult;
      }

      // 4. Dimensiones, distribuciones y productos que las requieren
      const uniqueProductCodes = new Set<string>(
        receiptHeaderData.receipt_detail.map(
          (detail: IReceiptDetail) => detail.product!.code,
        ),
      );

      const {
        dimensionsByProductCode,
        dimensionValuesByDimensionCode,
        parsedDistributionValues,
        productsWithDimensions,
      } = await fetchDimensionsAndDistributions(
        uniqueProductCodes,
        extraData.paymentMethod,
      );

      // 5. Datos de pago: cuentas, operaciones bancarias y términos
      const { accountsData, filteredBankOperations, normalizedPaymentTerms } =
        await fetchPaymentData(
          extraData.paymentMethod,
          extraData.providerCode!,
        );

      if (extraData.paymentMethod === PaymentMethodEnum.contado) {
        extraData.payOrderPayments = extraData.payOrderPayments || [];
      }

      // 6. Construir formData con productos inicializados
      /*
        TODO-JOEL: el autocompletado de la orden de compra implica que algunos valores de dimensiones
        podrian autocompletarse tambien. Las ordenes de compra listan productos, y esos productos pueden
        tener dimensiones.
      */
      const formData: ExtraData = {
        ...extraData,
        providerCode:
          extraData?.providerCode ?? receiptHeader?.sender?.tax_number,
        originDocument: extraData?.originDocument
          ? extraData.originDocument
          : receiptHeader?.purchase_order
          ? buyOrderData.find((oc: FinnegansOrdenCompraResponse) =>
              oc.IdentificacionExterna.split(' ').some(
                (value: any) => value === receiptHeader?.purchase_order,
              ),
            )?.IdentificacionExterna
          : undefined,
        originReceptions: extraData?.originReceptions ?? [],
        originCertificates: extraData?.originCertificates ?? [],
        products: receiptHeaderData.receipt_detail
          .filter(
            (detail: IReceiptDetail) =>
              detail.product?.code &&
              productsWithDimensions.includes(detail.product.code),
          )
          .map((detail: IReceiptDetail) => {
            const existedProduct = extraData?.products?.find(
              (product: ExtensionProduct) => product.detailId === detail.id,
            );
            return {
              code: detail.product?.code,
              dimensions: dimensionsByProductCode[detail.product!.code].map(
                (dimension: Dimension) => {
                  const currentDimension = existedProduct?.dimensions?.find(
                    (dim: ExtensionDimension) =>
                      dim.code === dimension.DIMENSIONCODIGO,
                  );
                  let defaultValues: any[] = [];
                  let defaultDistributionCode = '';
                  if (
                    (!currentDimension || !currentDimension.distribution) &&
                    dimension.DIMENSIONDISTRIBUCIONCODIGO
                  ) {
                    defaultDistributionCode =
                      dimension.DIMENSIONDISTRIBUCIONCODIGO;
                    const matchingDistribution = (
                      parsedDistributionValues[dimension.DIMENSIONCODIGO] || []
                    ).find(
                      (d) =>
                        d.dimensionDistributionCode === defaultDistributionCode,
                    );
                    if (matchingDistribution?.values)
                      defaultValues = matchingDistribution.values;
                  }
                  // Si existe currentDimension con una distribución ya guardada, se usa; si no, los defaults
                  return {
                    name: dimension.DIMENSIONNOMBRE,
                    code: dimension.DIMENSIONCODIGO,
                    values: currentDimension?.distribution
                      ? currentDimension.values
                      : defaultValues,
                    allowedIncompleteDistribution:
                      dimension.PERMITEDISTRIBUCIONINCOMPLETA,
                    distribution: currentDimension?.distribution
                      ? currentDimension.distribution
                      : defaultDistributionCode,
                  };
                },
              ),
              detailId: detail.id,
              description: detail.product?.description,
            };
          }),
        paymentTerm: extraData?.paymentTerm
          ? extraData.paymentTerm
          : normalizedPaymentTerms.find((term) => term.default)?.code ??
            (normalizedPaymentTerms.length === EnumsValues.ConstNumbers.one
              ? normalizedPaymentTerms[0].code
              : undefined),
        originDocumentType: filterCase,
        companyBranchCode: extraData?.companyBranchCode ?? undefined,
      };

      // 7. Settings de flujo (inputs, labor, certificados)
      const { dataSettingInputs, dataSettingLabor, dataSettingCertificate } =
        await fetchFlowSettings(receiptHeaderData.tenant_id);

      // 8. Recepciones
      const { receptionsData, validReceptions } = await fetchReceptions(
        availableDocuments,
        receiptHeaderData,
      );

      // 9. Certificados
      const { certificatesData, validCertificates } = await fetchCertificates(
        availableDocuments,
        receiptHeaderData,
      );

      // 10. Autocompletado desde webhook
      const finalFormData = await applyWebhookAutocomplete(
        formData,
        receiptHeaderData,
        accountsData,
        dimensionValuesByDimensionCode,
        parsedDistributionValues,
      );

      // 11. Actualizar estado
      setExtraData(finalFormData);
      setDimensions(dimensionsByProductCode);
      setDimensionValues(dimensionValuesByDimensionCode);
      setReceiptHeader(receiptHeaderData);
      setAvailableDocuments(groupedDocuments);
      setDistributions(parsedDistributionValues);
      setAccounts(accountsData);
      setBankOperations(filteredBankOperations);
      setPaymentTerms(normalizedPaymentTerms);
      setBranchList(activeBranches);
      setDocumentRelatedData(buyOrderData);
      setReceptionRelatedData(receptionsData);
      setCertificateRelatedData(certificatesData);
      setDataSettingInputs(dataSettingInputs);
      setDataSettingLabor(dataSettingLabor);
      setDataSettingCertificate(dataSettingCertificate);
      setDataSettingAdvanceInvoice(advanceInvoiceSettingResult);
      setAvailableReceptions(validReceptions);
      setAvailableCertificates(validCertificates);
    } catch (error) {
      setError(error);
    }
  };

  const saveExtensionData = async () => {
    return props.setExtensionDocumentInformation(extraData);
  };

  type ExtractedItem = {
    productCode: string;
    quantity: number;
    unitPrice?: number; // solo relevante para remitos de labores
  };

  const validateDocumentsAgainstInvoice = ({
    receiptHeader,
    selectedDocs,
    extractItems,
    validateAmounts,
  }: {
    receiptHeader: {
      receipt_detail?: IReceiptDetail[];
    };
    selectedDocs: any[];
    extractItems: (doc: any) => ExtractedItem[];
    validateAmounts: boolean;
  }) => {
    const invoiceDetails = receiptHeader?.receipt_detail;
    if (!invoiceDetails || invoiceDetails.length === 0) return true;

    // 1. cantidades en factura
    const invoiceQtyByProduct: Record<string, number> = {};
    const invoicePricesByProduct: Record<string, Set<number>> = {};

    invoiceDetails.forEach((inv) => {
      const code = inv.product?.code;
      if (!code) return;

      invoiceQtyByProduct[code] =
        (invoiceQtyByProduct[code] || 0) + (inv.quantity || 0);

      if (!invoicePricesByProduct[code]) {
        invoicePricesByProduct[code] = new Set();
      }

      if (inv.unit_price != null) {
        invoicePricesByProduct[code].add(inv.unit_price);
      }
    });

    // 2. cantidades acumuladas seleccionadas
    const usedQtyByProduct: Record<string, number> = {};

    for (const doc of selectedDocs) {
      const docItems = extractItems(doc);

      for (const docItem of docItems) {
        const productCode = docItem.productCode;

        // producto no existe en la factura
        if (!invoiceQtyByProduct[productCode]) {
          return false;
        }

        // LABORES: validar precio
        if (validateAmounts) {
          const prices = invoicePricesByProduct[productCode];
          if (!prices || !prices.has(docItem.unitPrice || 0)) {
            return false;
          }
        }

        // acumular
        usedQtyByProduct[productCode] =
          (usedQtyByProduct[productCode] || 0) + (docItem.quantity || 0);

        // no superar lo facturado
        if (usedQtyByProduct[productCode] > invoiceQtyByProduct[productCode]) {
          return false;
        }
      }
    }

    return true;
  };

  const handleFinishExtension = async () => {
    try {
      let dataToSave = extraData!;
      if (isCertificate) {
        if (
          !extraData?.originCertificates ||
          extraData.originCertificates.length === 0
        ) {
          Modal.error({
            title: 'Error de validación',
            content: 'Debe seleccionar al menos un certificado para continuar.',
          });
          return;
        }

        if (
          extraData?.originCertificates &&
          extraData.originCertificates.length > 0
        ) {
          const selectedCertificates = certificateRelatedData.filter((cert) =>
            extraData.originCertificates!.includes(cert.IdentificacionExterna),
          );

          // Validar que todos los certificados seleccionados pertenezcan al mismo workflow
          const distinctWorkflows = Array.from(
            new Set(
              selectedCertificates.map((c) => c.WorkflowCodigo).filter(Boolean),
            ),
          );

          if (distinctWorkflows.length > 1) {
            Modal.error({
              title: 'Error de validación',
              content:
                'Los certificados seleccionados son de workflows distintos',
            });
            return;
          }

          // Determinar el workflow de los certificados seleccionados
          const workflowCode =
            getWorkflowFromCertificates(selectedCertificates);

          if (workflowCode === WORKFLOW_SUBCONTRATISTA) {
            // --- Lógica específica: Certificado de Subcontratista (ConComSub) ---

            // Agrupamos los items de la factura por codigo de producto
            const invoiceTotals = receiptHeader?.receipt_detail?.reduce(
              (acc, detail) => {
                const code = detail.product?.code;
                if (!code) return acc;

                if (!acc[code]) {
                  acc[code] = { quantity: 0, amount: 0 };
                }
                acc[code].quantity += detail.quantity || 0;
                // Asumimos que el amount es precio * cantidad.
                // O si unit_price viene en el detail:
                const price = detail.unit_price || 0;
                acc[code].amount += price * (detail.quantity || 0);
                return acc;
              },
              {} as Record<string, { quantity: number; amount: number }>,
            );

            // Agrupamos los items de los certificados seleccionados por codigo de producto
            const certificateTotals = selectedCertificates.reduce(
              (acc, cert) => {
                cert.Items.forEach((item) => {
                  const code = item.ProductoCodigo;
                  if (!acc[code]) {
                    acc[code] = { quantity: 0, amount: 0 };
                  }
                  acc[code].quantity += item.Cantidad || 0;
                  const price = item.Precio || 0;
                  acc[code].amount += price * (item.Cantidad || 0);
                });
                return acc;
              },
              {} as Record<string, { quantity: number; amount: number }>,
            );

            // Comparamos
            const errors: string[] = [];
            if (invoiceTotals) {
              Object.keys(invoiceTotals).forEach((code) => {
                const invTotal = invoiceTotals[code];
                const certTotal = certificateTotals[code];

                if (!certTotal) {
                  errors.push(
                    `El producto ${code} está en la factura pero no en los certificados seleccionados.`,
                  );
                  return;
                }

                // Validamos cantidades
                if (Math.abs(invTotal.quantity - certTotal.quantity) > 0.001) {
                  errors.push(
                    `La cantidad del producto ${code} en la factura (${invTotal.quantity}) no coincide con la cantidad en los certificados (${certTotal.quantity}).`,
                  );
                }

                // Validamos montos
                if (Math.abs(invTotal.amount - certTotal.amount) > 0.01) {
                  errors.push(
                    `El monto del producto ${code} en la factura (${invTotal.amount.toFixed(
                      2,
                    )}) no coincide con el monto en los certificados (${certTotal.amount.toFixed(
                      2,
                    )}).`,
                  );
                }
              });
            }

            // Tambien verificar si hay productos en los certificados que no estan en la factura
            Object.keys(certificateTotals).forEach((code) => {
              if (!invoiceTotals || !invoiceTotals[code]) {
                errors.push(
                  `El producto ${code} está en los certificados pero no en la factura.`,
                );
              }
            });

            if (errors.length > 0) {
              Modal.error({
                title: 'Error de validación de certificados',
                content: (
                  <div>
                    <p>
                      No todos los productos de la factura pueden vincularse con
                      los certificados seleccionados.
                    </p>
                  </div>
                ),
              });
              return;
            }
          } else if (
            workflowCode === WORKFLOW_INSUMOS ||
            workflowCode === WORKFLOW_SERCON
          ) {
            // --- Lógica específica: Certificado de Insumos (CPRA-INS) y Servicios y Conceptos (CPRA-SERCON) ---

            // Limpiar flags de ciclos anteriores para este bloque
            dataToSave = {
              ...dataToSave,
              useCertItemsInPayload: undefined,
              anticipoProductCode: undefined,
              anticipoAmount: undefined,
            };

            // Sumar el total de los items de la factura
            const invoiceTotal =
              receiptHeader?.receipt_detail?.reduce(
                (acc, detail) =>
                  acc + (detail.unit_price || 0) * (detail.quantity || 0),
                0,
              ) ?? 0;

            // Sumar el total de los items de los certificados seleccionados (solo items con Precio > 0)
            const certTotal = selectedCertificates.reduce((acc, cert) => {
              return (
                acc +
                cert.Items.filter((item) => (item.Precio ?? 0) > 0).reduce(
                  (certAcc, item) =>
                    certAcc + (item.Precio ?? 0) * (item.Cantidad || 0),
                  0,
                )
              );
            }, 0);

            const allCertItems = selectedCertificates.flatMap((c) =>
              c.Items.filter((i) => (i.Precio ?? 0) > 0),
            );

            if (invoiceTotal > certTotal + 0.01) {
              // Error: la factura supera al certificado
              Modal.error({
                title: 'Error de validación de certificados',
                content: (
                  <div>
                    <p>
                      El monto total de la factura ({invoiceTotal.toFixed(2)})
                      supera el monto total de los certificados seleccionados (
                      {certTotal.toFixed(2)}).
                    </p>
                  </div>
                ),
              });
              return;
            } else if (certTotal > invoiceTotal + 0.01) {
              // CASO 3: el cert supera la factura → buscar producto de anticipo en el cert o en la OC
              const certWithAnticipo = selectedCertificates.find(
                (c) =>
                  (c.PorcentajeAnticipoFinanciero ?? 0) > 0 &&
                  !!c.ProductoAnticipoFinancieroCodigo,
              );
              const anticipoProductCode =
                certWithAnticipo?.ProductoAnticipoFinancieroCodigo ??
                documentRelatedData.find(
                  (oc) =>
                    (oc.PorcentajeAnticipoFinanciero ?? 0) > 0 &&
                    !!oc.ProductoAnticipoFinancieroCodigo,
                )?.ProductoAnticipoFinancieroCodigo;

              // El importe del anticipo es la diferencia exacta entre cert y factura
              const anticipoAmount = certTotal - invoiceTotal;

              if (anticipoProductCode) {
                const caso3Confirmed = await new Promise<boolean>((resolve) => {
                  Modal.confirm({
                    title:
                      'Confirmación: Registro de factura con certificado y anticipo',
                    width: 1000,
                    content: (
                      <div className="modal-confirm">
                        <div className="modal-confirm__content">
                          <p>
                            La factura se registrará con los ítems del
                            certificado más un ítem de anticipo financiero
                            negativo. El resultado final será igual al monto de
                            la factura:
                          </p>
                          <table
                            style={{
                              width: '100%',
                              borderCollapse: 'collapse',
                              fontSize: '13px',
                            }}
                          >
                            <thead>
                              <tr style={{ background: '#f0f0f0' }}>
                                <th
                                  style={{
                                    padding: '6px',
                                    border: '1px solid #d9d9d9',
                                    textAlign: 'left',
                                  }}
                                >
                                  Producto
                                </th>
                                <th
                                  style={{
                                    padding: '6px',
                                    border: '1px solid #d9d9d9',
                                    textAlign: 'right',
                                  }}
                                >
                                  Cant.
                                </th>
                                <th
                                  style={{
                                    padding: '6px',
                                    border: '1px solid #d9d9d9',
                                    textAlign: 'right',
                                  }}
                                >
                                  Precio Unit.
                                </th>
                                <th
                                  style={{
                                    padding: '6px',
                                    border: '1px solid #d9d9d9',
                                    textAlign: 'right',
                                  }}
                                >
                                  Importe
                                </th>
                                <th
                                  style={{
                                    padding: '6px',
                                    border: '1px solid #d9d9d9',
                                    textAlign: 'right',
                                  }}
                                >
                                  % cert.
                                </th>
                                <th
                                  style={{
                                    padding: '6px',
                                    border: '1px solid #d9d9d9',
                                    textAlign: 'left',
                                  }}
                                >
                                  Distribución
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {allCertItems.map((item, idx) => {
                                const importe =
                                  (item.Cantidad || 0) * (item.Precio || 0);
                                const pct =
                                  certTotal > 0
                                    ? (importe / certTotal) * 100
                                    : 0;
                                return (
                                  <tr key={idx}>
                                    <td
                                      style={{
                                        padding: '5px',
                                        border: '1px solid #d9d9d9',
                                      }}
                                    >
                                      {item.ProductoCodigo}
                                    </td>
                                    <td
                                      style={{
                                        padding: '5px',
                                        border: '1px solid #d9d9d9',
                                        textAlign: 'right',
                                      }}
                                    >
                                      {item.Cantidad}
                                    </td>
                                    <td
                                      style={{
                                        padding: '5px',
                                        border: '1px solid #d9d9d9',
                                        textAlign: 'right',
                                      }}
                                    >
                                      {(item.Precio ?? 0).toFixed(2)}
                                    </td>
                                    <td
                                      style={{
                                        padding: '5px',
                                        border: '1px solid #d9d9d9',
                                        textAlign: 'right',
                                      }}
                                    >
                                      {importe.toFixed(2)}
                                    </td>
                                    <td
                                      style={{
                                        padding: '5px',
                                        border: '1px solid #d9d9d9',
                                        textAlign: 'right',
                                      }}
                                    >
                                      {pct.toFixed(1)}%
                                    </td>
                                    <td
                                      style={{
                                        padding: '5px',
                                        border: '1px solid #d9d9d9',
                                        fontSize: '11px',
                                        whiteSpace: 'pre-line',
                                      }}
                                    >
                                      {(item.DimensionDistribucion ?? [])
                                        .flatMap((dim) =>
                                          (dim.distribucionItems ?? []).map(
                                            (di) =>
                                              `- ${di.codigo} ${di.porcentaje}%`,
                                          ),
                                        )
                                        .join('\n') || '—'}
                                    </td>
                                  </tr>
                                );
                              })}
                              <tr
                                style={{
                                  color: '#cf1322',
                                  fontStyle: 'italic',
                                }}
                              >
                                <td
                                  style={{
                                    padding: '5px',
                                    border: '1px solid #d9d9d9',
                                  }}
                                >
                                  {anticipoProductCode} (anticipo)
                                </td>
                                <td
                                  style={{
                                    padding: '5px',
                                    border: '1px solid #d9d9d9',
                                    textAlign: 'right',
                                  }}
                                >
                                  1
                                </td>
                                <td
                                  style={{
                                    padding: '5px',
                                    border: '1px solid #d9d9d9',
                                    textAlign: 'right',
                                  }}
                                >
                                  -{anticipoAmount.toFixed(2)}
                                </td>
                                <td
                                  style={{
                                    padding: '5px',
                                    border: '1px solid #d9d9d9',
                                    textAlign: 'right',
                                  }}
                                >
                                  -{anticipoAmount.toFixed(2)}
                                </td>
                                <td
                                  style={{
                                    padding: '5px',
                                    border: '1px solid #d9d9d9',
                                    textAlign: 'right',
                                  }}
                                >
                                  —
                                </td>
                                <td
                                  style={{
                                    padding: '5px',
                                    border: '1px solid #d9d9d9',
                                  }}
                                >
                                  —
                                </td>
                              </tr>
                            </tbody>
                            <tfoot>
                              <tr
                                style={{
                                  fontWeight: 'bold',
                                  background: '#fafafa',
                                }}
                              >
                                <td
                                  colSpan={3}
                                  style={{
                                    padding: '6px',
                                    border: '1px solid #d9d9d9',
                                  }}
                                >
                                  Total factura
                                </td>
                                <td
                                  style={{
                                    padding: '6px',
                                    border: '1px solid #d9d9d9',
                                    textAlign: 'right',
                                  }}
                                >
                                  {(certTotal - anticipoAmount).toFixed(2)}
                                </td>
                                <td
                                  style={{
                                    padding: '6px',
                                    border: '1px solid #d9d9d9',
                                  }}
                                />
                                <td
                                  style={{
                                    padding: '6px',
                                    border: '1px solid #d9d9d9',
                                  }}
                                />
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </div>
                    ),
                    okText: 'Confirmar',
                    cancelText: 'Cancelar',
                    icon: (
                      <span
                        className="material-symbols-outlined receipt-exclamation-icon"
                        translate="no"
                      >
                        info
                      </span>
                    ),
                    onOk: () => resolve(true),
                    onCancel: () => resolve(false),
                  });
                });

                if (!caso3Confirmed) return;

                dataToSave = {
                  ...dataToSave,
                  useCertItemsInPayload: true,
                  anticipoProductCode: anticipoProductCode!,
                  anticipoAmount,
                };
              } else if (
                extraData?.anticipoProductCode &&
                extraData?.anticipoAmount
              ) {
                // Datos de anticipo ya confirmados en sesión anterior; propagar sin re-confirmar.
                dataToSave = {
                  ...dataToSave,
                  useCertItemsInPayload: true,
                  anticipoProductCode: extraData.anticipoProductCode,
                  anticipoAmount: extraData.anticipoAmount,
                };
              } else {
                Modal.error({
                  title: 'Error de validación de certificados',
                  content: (
                    <div>
                      <p>
                        El monto total del certificado ({certTotal.toFixed(2)})
                        supera el de la factura ({invoiceTotal.toFixed(2)}) pero
                        no se encontró una orden de compra con anticipo
                        financiero asociada.
                      </p>
                    </div>
                  ),
                });
                return;
              }

              // Validación final CASO 3: certTotal − anticipo (calculado desde el cert) debe ≈ invoiceTotal
              {
                const certWithPct = selectedCertificates.find(
                  (c) =>
                    (c.PorcentajeAnticipoFinanciero ?? 0) > 0 &&
                    !!c.ProductoAnticipoFinancieroCodigo,
                );
                const anticipoPct =
                  certWithPct?.PorcentajeAnticipoFinanciero ?? 0;
                const recalcAnticipo =
                  anticipoPct > 0
                    ? (anticipoPct / 100) * certTotal
                    : dataToSave.anticipoAmount ?? 0;
                if (
                  Math.abs(certTotal - recalcAnticipo - invoiceTotal) > 0.01
                ) {
                  Modal.error({
                    title: 'Error de validación de certificados',
                    content: (
                      <div>
                        <p>
                          El total del certificado ({certTotal.toFixed(2)})
                          menos el anticipo calculado (
                          {recalcAnticipo.toFixed(2)}) no coincide con el total
                          de la factura ({invoiceTotal.toFixed(2)}). Vuelva a
                          seleccionar el certificado para confirmar la
                          operación.
                        </p>
                      </div>
                    ),
                  });
                  return;
                }
              }
            } else {
              // certTotal ≈ invoiceTotal → verificar CASO 2
              const uniqueProductCodes = new Set(
                allCertItems.map((i) => i.ProductoCodigo),
              );
              const isMultiItemSameCode =
                uniqueProductCodes.size === 1 && allCertItems.length > 1;

              if (isMultiItemSameCode) {
                const isDirectMatch = allCertItems.every((certItem) =>
                  receiptHeader?.receipt_detail?.some(
                    (d) =>
                      d.product?.code === certItem.ProductoCodigo &&
                      Math.abs((d.quantity || 0) - certItem.Cantidad) < 0.001,
                  ),
                );

                if (!isDirectMatch) {
                  // CASO 2: múltiples items del mismo ProductoCodigo, totales coinciden pero no 1:1 por cantidad
                  const caso2Confirmed = await new Promise<boolean>(
                    (resolve) => {
                      Modal.confirm({
                        title:
                          'Confirmación: Registro de factura con múltiples líneas de certificado',
                        width: 1000,
                        content: (
                          <div className="modal-confirm">
                            <div className="modal-confirm__content">
                              <p>
                                La factura se registrará con las siguientes
                                líneas del certificado (en lugar del ítem único
                                de factura). Cada línea tendrá su propia
                                distribución:
                              </p>
                              <table
                                style={{
                                  width: '100%',
                                  borderCollapse: 'collapse',
                                  fontSize: '13px',
                                }}
                              >
                                <thead>
                                  <tr style={{ background: '#f0f0f0' }}>
                                    <th
                                      style={{
                                        padding: '6px',
                                        border: '1px solid #d9d9d9',
                                        textAlign: 'left',
                                      }}
                                    >
                                      Producto
                                    </th>
                                    <th
                                      style={{
                                        padding: '6px',
                                        border: '1px solid #d9d9d9',
                                        textAlign: 'right',
                                      }}
                                    >
                                      Cant.
                                    </th>
                                    <th
                                      style={{
                                        padding: '6px',
                                        border: '1px solid #d9d9d9',
                                        textAlign: 'right',
                                      }}
                                    >
                                      Precio Unit.
                                    </th>
                                    <th
                                      style={{
                                        padding: '6px',
                                        border: '1px solid #d9d9d9',
                                        textAlign: 'right',
                                      }}
                                    >
                                      Importe
                                    </th>
                                    <th
                                      style={{
                                        padding: '6px',
                                        border: '1px solid #d9d9d9',
                                        textAlign: 'right',
                                      }}
                                    >
                                      % del total
                                    </th>
                                    <th
                                      style={{
                                        padding: '6px',
                                        border: '1px solid #d9d9d9',
                                        textAlign: 'left',
                                      }}
                                    >
                                      Distribución
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {allCertItems.map((item, idx) => {
                                    const importe =
                                      (item.Cantidad || 0) * (item.Precio || 0);
                                    const pct =
                                      certTotal > 0
                                        ? (importe / certTotal) * 100
                                        : 0;
                                    return (
                                      <tr key={idx}>
                                        <td
                                          style={{
                                            padding: '5px',
                                            border: '1px solid #d9d9d9',
                                          }}
                                        >
                                          {item.ProductoCodigo}
                                        </td>
                                        <td
                                          style={{
                                            padding: '5px',
                                            border: '1px solid #d9d9d9',
                                            textAlign: 'right',
                                          }}
                                        >
                                          {item.Cantidad}
                                        </td>
                                        <td
                                          style={{
                                            padding: '5px',
                                            border: '1px solid #d9d9d9',
                                            textAlign: 'right',
                                          }}
                                        >
                                          {(item.Precio ?? 0).toFixed(2)}
                                        </td>
                                        <td
                                          style={{
                                            padding: '5px',
                                            border: '1px solid #d9d9d9',
                                            textAlign: 'right',
                                          }}
                                        >
                                          {importe.toFixed(2)}
                                        </td>
                                        <td
                                          style={{
                                            padding: '5px',
                                            border: '1px solid #d9d9d9',
                                            textAlign: 'right',
                                          }}
                                        >
                                          {pct.toFixed(1)}%
                                        </td>
                                        <td
                                          style={{
                                            padding: '5px',
                                            border: '1px solid #d9d9d9',
                                            fontSize: '11px',
                                            whiteSpace: 'pre-line',
                                          }}
                                        >
                                          {(item.DimensionDistribucion ?? [])
                                            .flatMap((dim) =>
                                              (dim.distribucionItems ?? []).map(
                                                (di) =>
                                                  `- ${di.codigo} ${di.porcentaje}%`,
                                              ),
                                            )
                                            .join('\n') || '—'}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                                <tfoot>
                                  <tr
                                    style={{
                                      fontWeight: 'bold',
                                      background: '#fafafa',
                                    }}
                                  >
                                    <td
                                      colSpan={3}
                                      style={{
                                        padding: '6px',
                                        border: '1px solid #d9d9d9',
                                      }}
                                    >
                                      Total
                                    </td>
                                    <td
                                      style={{
                                        padding: '6px',
                                        border: '1px solid #d9d9d9',
                                        textAlign: 'right',
                                      }}
                                    >
                                      {certTotal.toFixed(2)}
                                    </td>
                                    <td
                                      style={{
                                        padding: '6px',
                                        border: '1px solid #d9d9d9',
                                        textAlign: 'right',
                                      }}
                                    >
                                      100.0%
                                    </td>
                                    <td
                                      style={{
                                        padding: '6px',
                                        border: '1px solid #d9d9d9',
                                      }}
                                    />
                                  </tr>
                                </tfoot>
                              </table>
                            </div>
                          </div>
                        ),
                        okText: 'Confirmar',
                        cancelText: 'Cancelar',
                        icon: (
                          <span
                            className="material-symbols-outlined receipt-exclamation-icon"
                            translate="no"
                          >
                            info
                          </span>
                        ),
                        onOk: () => resolve(true),
                        onCancel: () => resolve(false),
                      });
                    },
                  );

                  if (!caso2Confirmed) return;

                  dataToSave = {
                    ...dataToSave,
                    useCertItemsInPayload: true,
                  };
                }
                // isDirectMatch → CASO 1, flujo normal sin cambios
              }
              // !isMultiItemSameCode → CASO 1 u otro, flujo normal
            }
          } else if (workflowCode !== undefined) {
            // TODO: lógica de validación para otros tipos de certificado
          } else {
            // TODO: lógica de validación para certificados sin workflow definido
          }

          // Propagar el workflow al documento a guardar
          if (distinctWorkflows.length === 1) {
            dataToSave = {
              ...dataToSave,
              workflow: distinctWorkflows[0] as string,
            };
          }
        }
      }

      let modalResult = false;
      if (hasDifferenceInPayOrder) {
        modalResult = await new Promise<boolean>((resolve) => {
          Modal.confirm({
            content: (
              <div className="modal-confirm">
                <div className="modal-confirm__title">
                  Diferencia en los valores de la orden de pago
                </div>
                <div className="modal-confirm__content">
                  Los movimientos contables de la orden de pago no balancean, si
                  continua se creará una entrada de tipo “Otro” para solventar
                  la diferencia, dicho movimiento usará la cuenta definida en el
                  formulario de integración. ¿desea continuar?
                </div>
              </div>
            ),
            onOk: () => {
              resolve(true);
            },
            onCancel: () => {
              resolve(false);
            },
            icon: (
              <span
                className="material-symbols-outlined receipt-exclamation-icon"
                translate="no"
              >
                error
              </span>
            ),
          });
        });
      } else {
        modalResult = true;
      }

      if (!modalResult) return;

      // await saveExtensionData();
      await props.setExtensionDocumentInformation(dataToSave);
      await props.finishExtensionStep();
      props.setIsCompleted(props.resourceId, props.receiptCurrentStep);
      props.validateReceipts();
    } catch (error) {
      setError(error);
      return;
    }
  };

  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    initializeData().catch((error) => {
      setError(error);
    });
  }, []);

  const isAdvanceInvoiceFlow = dataSettingAdvanceInvoice?.value === 'true';

  useEffect(() => {
    if (extraData && extraData.products) {
      if (isAdvanceInvoiceFlow) {
        setIsValidForm(!!extraData.paymentTerm);
      } else {
        setIsValidForm(
          extraData.products.every((product) => {
            return validateFormByProduct(product);
          }) && !!extraData.paymentTerm,
        );
      }
    }
  }, [extraData, isAdvanceInvoiceFlow]);

  useEffect(() => {
    const originDocument = extraData?.originDocument;
    if (originDocument) {
      const originDocumentData = originDocument
        ? documentRelatedData.find(
            (oc: FinnegansOrdenCompraResponse) =>
              oc.IdentificacionExterna === originDocument,
          )
        : undefined;

      if (originDocumentData) {
        setExtraData({
          ...extraData,
          originDocumentData,
          workflow: originDocumentData.WorkflowCodigo,
        } as ExtraData);
      }
    } else {
      setExtraData({
        ...extraData,
        originDocumentData: undefined,
        workflow: undefined,
      } as ExtraData);
    }
  }, [extraData?.originDocument]);

  useEffect(() => {
    if (!extraData?.originReceptions) return;

    const selectedIds = extraData.originReceptions;

    const originReceptionData = receptionRelatedData.filter(
      (rec: FinnegansReceptionsResponse) =>
        selectedIds.includes(rec.IdentificacionExterna),
    );

    setExtraData({
      ...extraData,
      originReceptionData,
    } as ExtraData);
  }, [extraData?.originReceptions, receptionRelatedData]);

  useEffect(() => {
    if (!extraData?.originCertificates) return;

    const selectedIds = extraData.originCertificates;

    const originCertificateData = certificateRelatedData.filter(
      (rec: FinnegansCertificatesResponse) =>
        selectedIds.includes(rec.IdentificacionExterna),
    );

    setExtraData({
      ...extraData,
      originCertificateData,
    } as ExtraData);
  }, [extraData?.originCertificates, certificateRelatedData]);

  const isLabor = dataSettingLabor?.value === 'true';
  const isInputs = dataSettingInputs?.value === 'true';
  const isCertificate = dataSettingCertificate?.value === 'true';

  const certificateLabel = useMemo(() => {
    if (certificateRelatedData.length === 0) return 'Certificado';
    const workflowCode = certificateRelatedData[0]?.WorkflowCodigo;
    if (workflowCode === WORKFLOW_SUBCONTRATISTA)
      return 'Certificado de Subcontratista';
    if (workflowCode === WORKFLOW_INSUMOS) return 'Certificado de Insumos';
    if (workflowCode === WORKFLOW_SERCON)
      return 'Certificado de Servicios y Conceptos';
    return 'Certificado';
  }, [certificateRelatedData]);

  return (
    <div className="extension">
      {extraData && extraData.products ? (
        <>
          {!isInputs && !isLabor && !isCertificate && (
            <div className="extension-item">
              <span className="extension-item__title extension-label">
                {extraData.workflow
                  ? 'Documento de flete relacionado'
                  : isAdvanceInvoiceFlow
                  ? 'Orden de compra (anticipo)'
                  : 'Orden de compra'}
                <span className="extension-item__title--plus extension-label optional">
                  (Opcional)
                </span>
              </span>

              {availableDocuments.length > 0 && (
                <Select
                  options={availableDocuments.map(
                    (relatedDocument: ExtensionRelatedDocument) => ({
                      key: relatedDocument.code,
                      label: relatedDocument.name,
                      value: relatedDocument.code,
                    }),
                  )}
                  defaultValue={extraData?.originDocument}
                  size="large"
                  onChange={(value: string) => {
                    const relatedDocumentData = documentRelatedData.find(
                      (doc: FinnegansOrdenCompraResponse) =>
                        doc.IdentificacionExterna === value,
                    );
                    if (relatedDocumentData) {
                      const dimensionValuesForProduct: Record<string, {}[]> =
                        isAdvanceInvoiceFlow
                          ? {}
                          : getDimensionValuesForProductByOC(
                              relatedDocumentData,
                            );
                      setExtraData({
                        ...extraData,
                        originDocument: value,
                        isAdvanceInvoiceFlow: isAdvanceInvoiceFlow || undefined,
                        products: extraData.products?.map((product) => {
                          const productDimensionData: any =
                            dimensionValuesForProduct[product.code];

                          return {
                            ...product,
                            dimensions: product.dimensions.map((dimension) => {
                              const dimensionToUpdate =
                                productDimensionData?.find(
                                  (productDimension: any) =>
                                    productDimension.code === dimension.code,
                                );

                              return {
                                ...dimension,
                                distribution:
                                  dimensionToUpdate?.distribution || null,
                                values: dimensionToUpdate?.values || [],
                              };
                            }),
                          };
                        }),
                      });
                    } else {
                      setExtraData({
                        ...extraData,
                        originDocument: value,
                        isAdvanceInvoiceFlow: isAdvanceInvoiceFlow || undefined,
                      });
                    }
                  }}
                  allowClear
                />
              )}

              {!availableDocuments.length && (
                <Select
                  disabled
                  placeholder={
                    extraData.workflow
                      ? 'Sin documentos de flete disponibles para vincular'
                      : 'Sin órdenes de compra disponibles para vincular'
                  }
                />
              )}
            </div>
          )}
          {(isInputs || isLabor) && (
            <div className="extension-item">
              <span className="extension-item__title extension-label">
                {isLabor ? 'Rec. Labores' : 'Rec. Insumos'}
                <span className="extension-item__title--plus extension-label optional">
                  (Opcional)
                </span>
              </span>
              {availableReceptions.length > 0 ? (
                <Select
                  mode="multiple"
                  options={availableReceptions.map((rec) => ({
                    key: rec.code,
                    value: rec.code,
                    label: `${rec.name} - ${rec.receipt}`,
                  }))}
                  size="large"
                  value={extraData?.originReceptions ?? []}
                  onChange={(value: string[]) => {
                    if (!receiptHeader?.receipt_detail) {
                      setExtraData({
                        ...extraData,
                        originReceptions: value,
                        originReceptionsType:
                          DocumentoCategoriaId.RECIBO_FLETES,
                      });
                      return;
                    }
                    // Recepciones seleccionadas (sean de insumos o de labores)
                    const selectedReceptionData = receptionRelatedData.filter(
                      (rec) => value.includes(rec.IdentificacionExterna),
                    );
                    // validar que las cantidades/montos de los remitos seleccionados no superen lo facturado
                    const isValid = validateDocumentsAgainstInvoice({
                      receiptHeader,
                      selectedDocs: selectedReceptionData,
                      validateAmounts: isLabor,
                      extractItems: (rec) =>
                        rec.Items.map((item: any) => ({
                          productCode: item.ProductoCodigo,
                          quantity: item.Cantidad || 0,
                          unitPrice: item.Precio || 0,
                        })),
                    });
                    if (!isValid) {
                      // no actualiza originReceptions; el Select se mantiene con el valor anterior
                      return;
                    }
                    setExtraData({
                      ...extraData,
                      originReceptions: value,
                      originReceptionsType: DocumentoCategoriaId.RECIBO_FLETES,
                      products: extraData.products?.map((product) => ({
                        ...product,
                      })),
                    });
                  }}
                  allowClear
                />
              ) : (
                <Select
                  disabled
                  placeholder={
                    isLabor
                      ? 'Sin recepciones de labores disponibles para vincular'
                      : 'Sin recepciones de insumos disponibles para vincular'
                  }
                />
              )}
            </div>
          )}
          {isCertificate && certAmountWarning && (
            <Alert
              type="warning"
              message={certAmountWarning}
              showIcon
              style={{ marginBottom: 8 }}
            />
          )}
          {isCertificate && (
            <div className="extension-item">
              <span className="extension-item__title extension-label">
                {certificateLabel}
              </span>
              {availableCertificates.length > 0 ? (
                <Select
                  mode="multiple"
                  options={availableCertificates.map((cert) => ({
                    key: cert.code,
                    value: cert.code,
                    label: `${cert.name} - ${cert.receipt}`,
                  }))}
                  size="large"
                  value={extraData?.originCertificates ?? []}
                  onChange={(value: string[]) => {
                    if (!receiptHeader?.receipt_detail) {
                      setExtraData({
                        ...extraData,
                        originCertificates: value,
                        originCertificatesType:
                          DocumentoCategoriaId.CERTIFICADO_SUBCONTRATISTA,
                      });
                      return;
                    }

                    // Certificados seleccionados
                    const selectedCertificateData =
                      certificateRelatedData.filter((cert) =>
                        value.includes(cert.IdentificacionExterna),
                      );

                    // Determinar el workflow de los certificados seleccionados
                    const selectedWorkflowCode = getWorkflowFromCertificates(
                      selectedCertificateData,
                    );

                    let newProducts = extraData.products;

                    if (selectedWorkflowCode === WORKFLOW_SUBCONTRATISTA) {
                      // --- Lógica específica: Certificado de Subcontratista (ConComSub) ---

                      // validar que las cantidades/montos de los certificados seleccionados no superen lo facturado
                      const isValid = validateDocumentsAgainstInvoice({
                        receiptHeader,
                        selectedDocs: selectedCertificateData,
                        validateAmounts: true,
                        extractItems: (cert) =>
                          cert.Items.map((item: any) => ({
                            productCode: item.ProductoCodigo,
                            quantity: item.Cantidad || 0,
                            unitPrice: item.Precio || 0,
                          })),
                      });

                      if (!isValid) {
                        // no actualiza originCertificates; el Select se mantiene con el valor anterior
                        return;
                      }

                      // Flat map de todos los items de los certificados seleccionados con sus detalles para la coincidencia
                      // NOTA: NO usamos getDimensionValuesForProductByCertificate porque agrupa por código de producto,
                      // lo que mezcla los valores de dimensión cuando hay múltiples items con el mismo código.
                      // En su lugar, extraemos las dimensiones directamente de cada item.
                      const allCertificateItems =
                        selectedCertificateData.flatMap((cert) =>
                          cert.Items.map((item) => {
                            // Extraer dimensionValues directamente del item individual
                            const itemDimensionValues: any[] = [];
                            item.DimensionDistribucion?.forEach(
                              (dimension: FinnegansDimension) => {
                                if (
                                  Array.isArray(dimension.distribucionItems)
                                ) {
                                  const distribution =
                                    dimension.distribucionItems.map(
                                      (distributionItem) => ({
                                        code: distributionItem.codigo,
                                        percentage: distributionItem.porcentaje,
                                      }),
                                    );
                                  itemDimensionValues.push({
                                    code: dimension.dimensionCodigo,
                                    distribution:
                                      dimension.distribucionCodigo || '',
                                    values: distribution,
                                  });
                                }
                              },
                            );

                            return {
                              productCode: item.ProductoCodigo,
                              quantity: item.Cantidad,
                              price: item.Precio,
                              amount: (item.Cantidad || 0) * (item.Precio || 0),
                              description: (item as any).Descripcion || '',
                              dimensions: item.DimensionDistribucion,
                              dimensionValues: itemDimensionValues,
                              _matched: false, // flag para evitar reutilizar un candidato
                            };
                          }),
                        );

                      // --- Función auxiliar: similitud por palabras ---
                      const wordSimilarity = (a: string, b: string): number => {
                        const normalize = (s: string) =>
                          s.toLowerCase().trim().split(/\s+/).filter(Boolean);
                        const wordsA = normalize(a);
                        const wordsB = normalize(b);
                        if (wordsA.length === 0 && wordsB.length === 0)
                          return 1;
                        if (wordsA.length === 0 || wordsB.length === 0)
                          return 0;
                        const setB = new Set(wordsB);
                        const matchCount = wordsA.filter((w) =>
                          setB.has(w),
                        ).length;
                        return (
                          matchCount / Math.max(wordsA.length, wordsB.length)
                        );
                      };

                      // --- Función auxiliar: aplicar dimensiones del candidato al producto ---
                      const applyDimensions = (
                        product: ExtensionProduct,
                        candidate: typeof allCertificateItems[0],
                      ) => {
                        const productDimensionData: any =
                          candidate.dimensionValues;

                        if (
                          !productDimensionData ||
                          productDimensionData.length === 0
                        ) {
                          return product;
                        }

                        return {
                          ...product,
                          dimensions: product.dimensions.map((dimension) => {
                            const dimensionToUpdate =
                              productDimensionData?.find(
                                (pd: any) => pd.code === dimension.code,
                              );
                            if (dimensionToUpdate) {
                              return {
                                ...dimension,
                                values: dimensionToUpdate.values || [],
                              };
                            }
                            return dimension;
                          }),
                        };
                      };

                      // --- Matching de 3 niveles ---
                      newProducts = extraData.products?.map((product) => {
                        const invoiceDetail =
                          receiptHeader.receipt_detail?.find(
                            (d) => d.id === product.detailId,
                          );
                        if (!invoiceDetail) {
                          return product;
                        }

                        // Candidatos no consumidos con mismo código de producto
                        const candidates = allCertificateItems.filter(
                          (item) =>
                            !item._matched && item.productCode === product.code,
                        );

                        if (candidates.length === 0) return product;

                        // NIVEL 1: Si hay un solo candidato con ese código → match directo
                        if (candidates.length === 1) {
                          candidates[0]._matched = true;
                          return applyDimensions(product, candidates[0]);
                        }

                        // NIVEL 2: Filtrar por cantidad y precio unitario
                        const byQtyPrice = candidates.filter(
                          (item) =>
                            Math.abs(
                              (item.quantity || 0) -
                                (invoiceDetail.quantity || 0),
                            ) < 0.001 &&
                            Math.abs(
                              (item.price || 0) -
                                (invoiceDetail.unit_price || 0),
                            ) < 0.01,
                        );

                        if (byQtyPrice.length === 1) {
                          byQtyPrice[0]._matched = true;
                          return applyDimensions(product, byQtyPrice[0]);
                        }

                        // NIVEL 3: Similitud de descripción (>= 95%)
                        const invoiceDesc =
                          invoiceDetail.description ||
                          product.description ||
                          '';
                        const pool =
                          byQtyPrice.length > 1 ? byQtyPrice : candidates;

                        let bestCandidate:
                          | typeof allCertificateItems[0]
                          | null = null;
                        let bestScore = 0;

                        for (const c of pool) {
                          const score = wordSimilarity(
                            invoiceDesc,
                            c.description,
                          );
                          if (score > bestScore) {
                            bestScore = score;
                            bestCandidate = c;
                          }
                        }

                        if (
                          bestCandidate &&
                          bestScore >= DESCRIPTION_MATCH_THRESHOLD
                        ) {
                          bestCandidate._matched = true;
                          return applyDimensions(product, bestCandidate);
                        }
                        return product;
                      });
                    } else if (
                      selectedWorkflowCode === WORKFLOW_INSUMOS ||
                      selectedWorkflowCode === WORKFLOW_SERCON
                    ) {
                      // --- Lógica específica: Certificado de Insumos (CPRA-INS) y Servicios y Conceptos (CPRA-SERCON) ---

                      // Items del certificado disponibles (sin vinculacionDestino y con Precio > 0)
                      const availableCertItems =
                        selectedCertificateData.flatMap((cert) =>
                          cert.Items.filter(
                            (item) =>
                              !item.vinculacionDestino &&
                              (item.Precio ?? 0) > 0,
                          ),
                        );

                      // Totales para validación de monto
                      const invoiceTotal =
                        receiptHeader.receipt_detail?.reduce(
                          (acc, d) =>
                            acc + (d.unit_price || 0) * (d.quantity || 0),
                          0,
                        ) ?? 0;
                      const certTotal = availableCertItems.reduce(
                        (acc, i) => acc + (i.Precio ?? 0) * (i.Cantidad || 0),
                        0,
                      );

                      if (invoiceTotal > certTotal + 0.01) {
                        setCertAmountWarning(
                          `El monto total de la factura ($${invoiceTotal.toFixed(
                            2,
                          )}) supera el monto total de los certificados seleccionados ($${certTotal.toFixed(
                            2,
                          )}).`,
                        );
                      } else {
                        setCertAmountWarning(null);
                      }

                      // Helper: ¿el producto ya tiene dimensiones con valores configurados?
                      const productHasValues = (p: ExtensionProduct) =>
                        p.dimensions.some((d) => d.values.length > 0);

                      // Helper: convertir DimensionDistribucion de un cert item a ExtensionDimension[]
                      const dimsFromCertItem = (
                        certItem: CertificateItem,
                      ): ExtensionDimension[] =>
                        (certItem.DimensionDistribucion ?? [])
                          .filter(
                            (dim) => (dim.distribucionItems?.length ?? 0) > 0,
                          )
                          .map((dim) => ({
                            name: '',
                            code: dim.dimensionCodigo,
                            distribution: dim.distribucionCodigo || '',
                            values: dim.distribucionItems.map((di) => ({
                              code: di.codigo,
                              percentage: di.porcentaje,
                            })),
                          }));

                      // --- Detección de caso ---
                      const isCase1 =
                        newProducts !== undefined &&
                        availableCertItems.length > 0 &&
                        availableCertItems.length === newProducts.length &&
                        newProducts.every((p) =>
                          availableCertItems.some(
                            (ci) => ci.ProductoCodigo === p.code,
                          ),
                        );

                      const isCase2 =
                        !isCase1 &&
                        availableCertItems.length > 1 &&
                        (newProducts?.length ?? 0) === 1 &&
                        certTotal > 0 &&
                        certTotal >= invoiceTotal - 0.01;

                      if (isCase1) {
                        // --- Caso 1: 1:1 — copiar dims del cert item ---
                        // Matching multi-nivel: primero por código único, luego por cantidad+precio
                        const usedIndices = new Set<number>();
                        newProducts = newProducts?.map((product) => {
                          if (productHasValues(product)) return product;

                          const invoiceDetail =
                            receiptHeader.receipt_detail?.find(
                              (d) => d.id === product.detailId,
                            );

                          // Candidatos con mismo código no usados
                          const candidateIndices = availableCertItems
                            .map((_ci, idx) => idx)
                            .filter(
                              (idx) =>
                                !usedIndices.has(idx) &&
                                availableCertItems[idx].ProductoCodigo ===
                                  product.code,
                            );

                          if (candidateIndices.length === 0) return product;

                          let matchIdx: number;

                          if (candidateIndices.length === 1) {
                            // Nivel 1: único candidato
                            matchIdx = candidateIndices[0];
                          } else if (invoiceDetail) {
                            // Nivel 2: filtrar por cantidad y precio unitario
                            const byQtyPrice = candidateIndices.filter(
                              (idx) => {
                                const ci = availableCertItems[idx];
                                return (
                                  Math.abs(
                                    (ci.Cantidad || 0) -
                                      (invoiceDetail.quantity || 0),
                                  ) < 0.001 &&
                                  Math.abs(
                                    (ci.Precio || 0) -
                                      (invoiceDetail.unit_price || 0),
                                  ) < 0.01
                                );
                              },
                            );
                            matchIdx =
                              byQtyPrice.length >= 1
                                ? byQtyPrice[0]
                                : candidateIndices[0];
                          } else {
                            // Fallback: primer disponible
                            matchIdx = candidateIndices[0];
                          }

                          usedIndices.add(matchIdx);

                          const certDims = dimsFromCertItem(
                            availableCertItems[matchIdx],
                          );
                          if (certDims.length === 0) return product;

                          return {
                            ...product,
                            dimensions: product.dimensions.map((dim) => {
                              const certDim = certDims.find(
                                (cd) => cd.code === dim.code,
                              );
                              return certDim
                                ? { ...dim, values: certDim.values }
                                : dim;
                            }),
                          };
                        });
                      } else if (isCase2) {
                        // --- Caso 2: N cert items → 1 invoice item ---
                        // Crear una entrada de producto por cada cert item con sus propias
                        // dimensiones al 100% (distribución individual del cert item).
                        const baseProduct = newProducts?.[0];
                        if (baseProduct) {
                          newProducts = availableCertItems.map((ci) => {
                            const certDims = dimsFromCertItem(ci);
                            return {
                              ...baseProduct,
                              description:
                                ci.Descripcion || baseProduct.description,
                              dimensions:
                                certDims.length > 0
                                  ? baseProduct.dimensions.map((dim) => {
                                      const certDim = certDims.find(
                                        (cd) => cd.code === dim.code,
                                      );
                                      return certDim
                                        ? { ...dim, values: certDim.values }
                                        : dim;
                                    })
                                  : baseProduct.dimensions,
                            };
                          });
                        }
                      }
                    } else if (selectedWorkflowCode !== undefined) {
                      // TODO: lógica de selección para otros tipos de certificado
                    } else {
                      // TODO: lógica de selección para certificados sin workflow definido
                    }

                    // Buscar la categoria original del primer certificado seleccionado
                    const availableOriginalDocs =
                      availableDocuments as unknown as {
                        TRANSACCIONCATEGORIAID: number;
                        IDENTIFICACIONEXTERNA: string;
                        [key: string]: any;
                      }[];

                    const firstSelectedDoc = availableOriginalDocs.find(
                      (doc) => doc.IDENTIFICACIONEXTERNA === value[0],
                    );

                    const originCertificatesType = firstSelectedDoc
                      ? firstSelectedDoc.TRANSACCIONCATEGORIAID
                      : DocumentoCategoriaId.CERTIFICADO_SUBCONTRATISTA;

                    setExtraData({
                      ...extraData,
                      originCertificates: value,
                      originCertificatesType: originCertificatesType,
                      products: newProducts,
                    });
                  }}
                  allowClear
                />
              ) : (
                <Select
                  disabled
                  placeholder={'Sin certificados disponibles para vincular'}
                />
              )}
            </div>
          )}
          <div className="extension-item">
            <span className="extension-item__title extension-label">
              Sucursal
            </span>
            <Select
              options={[
                { label: 'Por defecto', value: '' },
                ...branchList.map((branch) => ({
                  label: branch.nombre,
                  value: branch.codigo,
                })),
              ]}
              value={extraData.companyBranchCode || ''}
              onChange={(value: string) => {
                setExtraData({
                  ...extraData,
                  companyBranchCode: value || undefined,
                });
              }}
            />
          </div>
          <div className="extension-item">
            <span className="extension-item__title extension-label">
              Plazo de pago
            </span>
            <Select
              options={paymentTerms.map((term) => ({
                label: term.name,
                value: term.code,
              }))}
              defaultValue={
                extraData.paymentTerm
                  ? extraData.paymentTerm
                  : paymentTerms.length === EnumsValues.ConstNumbers.one
                  ? paymentTerms[0].name
                  : undefined
              }
              onChange={(value: string) => {
                setExtraData({
                  ...extraData,
                  paymentTerm: value,
                });
              }}
            />
          </div>
          <div className="extension-item">
            <span className="extension-item__title extension-label">
              Observaciones
            </span>
            <Input
              maxLength={255}
              value={extraData.description || ''}
              onChange={(e) => {
                setExtraData({
                  ...extraData,
                  description: e.target.value || undefined,
                });
              }}
              placeholder="Observaciones (máx. 255 caracteres)"
            />
          </div>
          {extraData.products.length > 1 && (
            <Stepper
              currentStep={currentStep}
              setCurrentStep={setCurrentStep}
              products={extraData.products || []}
              extraData={extraData}
              setShowStepValidateMessage={setShowStepValidateMessage}
            />
          )}
          {extraData.products[currentStep] &&
            dimensions &&
            dimensionValues &&
            distributions && (
              <>
                <ProductStep
                  key={currentStep}
                  dimensions={dimensions[extraData.products[currentStep].code]}
                  dimensionValues={dimensionValues}
                  setExtraData={setExtraData}
                  extraData={extraData}
                  currentProduct={extraData?.products?.[currentStep]}
                  distributions={distributions}
                />
                {extraData.products && extraData.products.length === 0 && (
                  <div>
                    <Alert
                      message="No hay productos que requieran dimensiones."
                      style={{ borderRadius: 4 }}
                    />
                  </div>
                )}

                {showStepValidateMessage && (
                  <div style={{ marginTop: 20 }}>
                    <Alert
                      message={
                        <div className="validation-alert">
                          <span>
                            Verificar que cada producto tenga al menos un valor
                            en todas sus dimensiones y que la suma de los
                            porcentajes por dimensión sea 100%.
                          </span>
                          <span>
                            Verficar que se haya seleccionado un valor para el
                            campo "Plazo de pago"
                          </span>
                        </div>
                      }
                      type="error"
                      showIcon
                      style={{ borderRadius: 4 }}
                    />
                  </div>
                )}

                {extraData.products.length > 1 && (
                  <div style={{ marginTop: 20, textAlign: 'right' }}>
                    <ButtonSecondary
                      disabled={currentStep === 0}
                      onClick={() => {
                        const pastProductIsValid =
                          currentStep > 0 &&
                          validateFormByProduct(
                            extraData.products![currentStep - 1],
                          );
                        if (currentProductIsValid || pastProductIsValid) {
                          setCurrentStep((prev) => prev - 1);
                        } else {
                          setShowStepValidateMessage(true);
                        }
                      }}
                    >
                      Anterior
                    </ButtonSecondary>
                    <ButtonPrimary
                      style={{ marginLeft: 10 }}
                      disabled={currentStep === extraData.products.length - 1}
                      onClick={() => {
                        const nextProductIsValid =
                          extraData.products &&
                          currentStep < extraData.products.length - 1 &&
                          validateFormByProduct(
                            extraData.products![currentStep + 1],
                          );
                        if (currentProductIsValid || nextProductIsValid) {
                          setCurrentStep((prev) => prev + 1);
                        } else {
                          setShowStepValidateMessage(true);
                        }
                      }}
                    >
                      Siguiente ítem
                    </ButtonPrimary>
                  </div>
                )}
              </>
            )}
          {extraData.paymentMethod &&
            extraData.payOrderPayments &&
            extraData.paymentMethod === PaymentMethodEnum.contado && (
              <div className="payment">
                <div className="payment__title extension-label">
                  Cancelación de la orden de pago
                </div>
                <PaymentTable
                  extraData={extraData}
                  setExtraData={setExtraData}
                  accounts={accounts}
                  bankOperations={bankOperations}
                  receiptHeader={receiptHeader}
                  setHasDifferenceInPayOrder={setHasDifferenceInPayOrder}
                  queryExternalService={props.queryExternalService}
                />
              </div>
            )}
          <div style={{ textAlign: 'right' }}>
            <Tooltip
              trigger="hover"
              title={
                isValidForm
                  ? undefined
                  : `Controlar que todas las dimensiones de todos los productos tengan al menos un valor y que la sumatoria de los porcentajes por dimension sea 100
                  Controlar que se haya seleccionado un valor para el campo "Plazo de pago"
                  `
              }
              placement="topRight"
            >
              <ButtonAction
                style={{ marginLeft: 10 }}
                onClick={handleFinishExtension}
                disabled={!isValidForm}
              >
                Guardar y continuar
              </ButtonAction>
            </Tooltip>
          </div>
        </>
      ) : (
        <div className="extension__spin">
          <Spin size="large" />
        </div>
      )}
    </div>
  );
}

/*
 STEPPER
*/

interface StepperProps {
  currentStep: number;
  setCurrentStep: (step: number) => void;
  setShowStepValidateMessage: Dispatch<SetStateAction<boolean>>;
  products: ExtensionProduct[];
  extraData: ExtraData;
}

export const Stepper = (props: StepperProps) => {
  const {
    currentStep,
    setCurrentStep,
    products,
    extraData,
    setShowStepValidateMessage,
  } = props;
  const { Step } = Steps;

  const getStepIcon = (
    index: number,
    status: 'finish' | 'process' | 'wait',
  ) => {
    if (status === 'finish') {
      // For finished steps, show the step number with selected styles
      return (
        <div className="custom-step-icon custom-step-icon-finish">
          {index + 1}
        </div>
      );
    } else if (status === 'process') {
      // For current step, show a circle
      return (
        <div className="custom-step-icon custom-step-icon-process">
          <div className="custom-step-icon-process-inner"></div>
        </div>
      );
    }
    // For waiting steps, return undefined to use the default icon
    return undefined;
  };

  return (
    <Steps
      className="extension__steps"
      current={currentStep}
      onChange={(step) => {
        const currentProduct = extraData.products![currentStep];
        const currentProductIsValid = validateFormByProduct(currentProduct!);
        const stepProductIsValid =
          extraData.products?.[step] &&
          validateFormByProduct(extraData.products![step]);
        if (currentProductIsValid || stepProductIsValid) {
          setCurrentStep(step);
        } else {
          setShowStepValidateMessage(true);
        }
      }}
    >
      {products?.map((product: ExtensionProduct, index: number) => {
        const status =
          currentStep === index && validateFormByProduct(product)
            ? 'finish'
            : currentStep === index
            ? 'process'
            : validateFormByProduct(product)
            ? 'finish'
            : 'wait';
        return (
          <Step
            key={product.detailId}
            status={status}
            icon={getStepIcon(index, status)}
          />
        );
      })}
    </Steps>
  );
};

/*
 PRODUCT STEP
*/

interface ProductStepProps {
  currentProduct: ExtensionProduct;
  dimensionValues: Record<string, DimensionValue[]>;
  dimensions: Dimension[];
  setExtraData: React.Dispatch<React.SetStateAction<ExtraData | undefined>>;
  extraData: ExtraData;
  distributions: Record<string, Distribution[]>;
}

export const ProductStep = (props: ProductStepProps) => {
  const {
    currentProduct,
    dimensionValues,
    dimensions,
    setExtraData,
    extraData,
    distributions,
  } = props;

  return (
    <div className="product-step-container">
      <div className="product-step__title">
        <span className="product-step__title--left">
          {currentProduct?.description}
        </span>
        <Divider
          type="vertical"
          style={{
            fontSize: '24px',
            borderLeft: '3.3px solid black',
            borderRadius: '2px',
          }}
        />
        <span className="product-step__title--right">
          {currentProduct?.code}
        </span>
      </div>

      <div className="product-step-dimension">
        {dimensions?.map((dimension: Dimension) => (
          <div
            className="product-step-dimension__item"
            key={dimension.DIMENSIONCODIGO}
          >
            <span className="product-step-dimension__title">
              Dimensión: {dimension.DIMENSIONNOMBRE}
            </span>
            <Divider style={{ margin: '5px 0 15px' }} />

            <DistributionBlock
              availableValues={dimensionValues[dimension.DIMENSIONCODIGO] || []}
              dimensionCode={dimension.DIMENSIONCODIGO}
              dimensionValues={
                extraData.products
                  ?.find(
                    (prod: ExtensionProduct) =>
                      prod.detailId === currentProduct.detailId,
                  )
                  ?.dimensions.find(
                    (dim: ExtensionDimension) =>
                      dim.code === dimension.DIMENSIONCODIGO,
                  )?.values || []
              }
              distributions={distributions[dimension.DIMENSIONCODIGO] || []}
              setExtraData={setExtraData}
              extraData={extraData}
              currentProduct={currentProduct}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

/////////////////////////////////////////////////////////////
/*
 DISTRIBUTION BLOCK
*/

interface DistributionBlockProps {
  availableValues: DimensionValue[];
  dimensionCode: string;
  dimensionValues: ExtensionDimensionValue[];
  distributions: Distribution[];
  setExtraData: React.Dispatch<React.SetStateAction<ExtraData | undefined>>;
  extraData: ExtraData;
  currentProduct: ExtensionProduct;
}

const DistributionBlock = (props: DistributionBlockProps) => {
  const {
    availableValues,
    dimensionCode,
    dimensionValues,
    distributions,
    setExtraData,
    extraData,
    currentProduct,
  } = props;

  const selectedDistribution =
    extraData?.products
      ?.find((product) => product.detailId === currentProduct.detailId)
      ?.dimensions?.find(
        (dim: ExtensionDimension) => dim.code === dimensionCode,
      )?.distribution || DISTRIBUTION_MANUAL;

  const isDisabled = useMemo(
    () => selectedDistribution !== DISTRIBUTION_MANUAL,
    [selectedDistribution],
  );

  const getDistributionValues = () => {
    const defaultValues = [
      { value: DISTRIBUTION_MANUAL, label: 'Distribución manual' },
    ];
    const rest = distributions.map((distribution: Distribution) => ({
      value: distribution.dimensionDistributionCode,
      label: distribution.dimensionDistributionName,
    }));

    return [...defaultValues, ...rest];
  };

  const handleAddDistributionValue = (dimensionCode: string) => {
    const newEntry = {
      code: '',
      percentage: 0,
    };

    setExtraData({
      ...extraData,
      products: extraData.products?.map((prod: ExtensionProduct) => ({
        ...prod,
        dimensions: prod.dimensions.map((dim: ExtensionDimension) => ({
          ...dim,
          values:
            prod.detailId === currentProduct.detailId &&
            dim.code === dimensionCode
              ? [...dim.values, newEntry]
              : dim.values,
        })),
      })),
    });
  };

  const handleRemoveDistributionValue = (
    dimensionCode: string,
    index: number,
  ) => {
    setExtraData({
      ...extraData,
      products: extraData.products?.map((prod: ExtensionProduct) => ({
        ...prod,
        dimensions: prod.dimensions.map((dim: ExtensionDimension) => ({
          ...dim,
          values:
            prod.detailId === currentProduct.detailId &&
            dim.code === dimensionCode
              ? dim.values.filter((_value, idx) => idx !== index)
              : dim.values,
        })),
      })),
    });
  };

  const handleUpdateDistributionValue = (
    dimensionCode: string,
    index: number,
    field: keyof ExtensionDimensionValue,
    value: string | number,
  ) => {
    setExtraData({
      ...extraData,
      products: extraData.products?.map((prod: ExtensionProduct) => ({
        ...prod,
        dimensions: prod.dimensions.map((dim: ExtensionDimension) => ({
          ...dim,
          values:
            prod.detailId === currentProduct.detailId &&
            dim.code === dimensionCode
              ? dim.values.map((dimValue, idx) =>
                  idx === index ? { ...dimValue, [field]: value } : dimValue,
                )
              : dim.values,
        })),
      })),
    });
  };

  const handleDistributionChange = (distributionCode: string) => {
    setExtraData((prev) => {
      const newProducts = prev?.products?.map((product) => {
        if (product.detailId !== currentProduct.detailId) return product;
        return {
          ...product,
          dimensions: product.dimensions.map((dimension) => {
            if (dimension.code !== dimensionCode) return dimension;
            const selectedDistribution = distributions?.find(
              (d) => d.dimensionDistributionCode === distributionCode,
            );

            return {
              ...dimension,
              distribution:
                distributionCode !== DISTRIBUTION_MANUAL
                  ? distributionCode
                  : '',
              values: selectedDistribution?.values || [],
            };
          }),
        };
      });

      if (!newProducts) return prev;
      return {
        ...prev,
        paymentMethod: prev!.paymentMethod as PaymentMethodEnum,
        products: newProducts,
      };
    });
  };

  return (
    <div className="distribution-block">
      <label className="extension-label">Distribución</label>
      <Select
        showSearch
        placeholder="Buscar distribución"
        optionFilterProp="label"
        filterOption={(input, option) =>
          option
            ? option.label.toLowerCase().includes(input.toLowerCase())
            : false
        }
        className="distribution-block__distribution-select"
        options={getDistributionValues()}
        value={selectedDistribution}
        size="large"
        onChange={(value: string) => handleDistributionChange(value)}
      />
      {dimensionValues.length > 0 && (
        <>
          <div
            className="distribution-block__header"
            style={{
              gridTemplateColumns: isDisabled ? '1fr 1fr' : '1fr 1fr 30px',
            }}
          >
            <div className="extension-label">Valor</div>
            <div className="extension-label">Porcentaje</div>
            <div></div>
          </div>

          {dimensionValues.map(
            (dimensionValue: ExtensionDimensionValue, idx: number) => (
              <div
                key={`${dimensionValue.code}-${idx}-${selectedDistribution}`}
                className="distribution-block__item"
                style={{
                  gridTemplateColumns: isDisabled ? '1fr 1fr' : '1fr 1fr 30px',
                }}
              >
                <Select
                  showSearch
                  placeholder="Buscar valor"
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    !!option?.label?.toLowerCase().includes(input.toLowerCase())
                  }
                  options={availableValues.map((item) => ({
                    value: item.CODIGO,
                    label: item.NOMBRE,
                  }))}
                  defaultValue={dimensionValue.code}
                  onChange={(value) =>
                    handleUpdateDistributionValue(
                      dimensionCode,
                      idx,
                      'code',
                      value,
                    )
                  }
                  disabled={isDisabled}
                >
                  {availableValues
                    .filter(
                      (item) =>
                        !dimensionValues.some(
                          (val) => val.code === item.CODIGO,
                        ) || item.CODIGO === dimensionValue.code,
                    )
                    .map((item) => (
                      <Select.Option
                        key={`${dimensionCode}-${item.CODIGO}`}
                        value={item.CODIGO}
                      >
                        {item.NOMBRE}
                      </Select.Option>
                    ))}
                </Select>
                <Input
                  type="number"
                  defaultValue={dimensionValue.percentage}
                  onChange={(e) =>
                    handleUpdateDistributionValue(
                      dimensionCode,
                      idx,
                      'percentage',
                      Number(e.target.value),
                    )
                  }
                  disabled={isDisabled}
                />
                {!isDisabled && (
                  <Button
                    className="distribution-block__action-button"
                    onClick={() =>
                      handleRemoveDistributionValue(dimensionCode, idx)
                    }
                    type="link"
                  >
                    <MinusCircleOutlined className="distribution-block__action-icon distribution-block__item-content--icon" />
                  </Button>
                )}
              </div>
            ),
          )}
        </>
      )}
      {!isDisabled && (
        <Button
          className="distribution-block__action-button"
          type="link"
          onClick={() => handleAddDistributionValue(dimensionCode)}
        >
          <PlusCircleOutlined className="distribution-block__action-icon" />
          <span className="distribution-block__action-label">
            Agregar nuevo valor
          </span>
        </Button>
      )}
    </div>
  );
};

interface PaymentTableProps {
  extraData: ExtraData;
  setExtraData: (extraData: ExtraData) => void;
  accounts: AccountValue[];
  bankOperations: OperacionesBancarias[];
  receiptHeader: IReceiptHeader | undefined;
  setHasDifferenceInPayOrder: (hasDifference: boolean) => void;
  queryExternalService: FrontendExtensionProps['queryExternalService'];
}

const PaymentTable = (props: PaymentTableProps) => {
  const {
    extraData,
    setExtraData,
    accounts,
    bankOperations,
    receiptHeader,
    setHasDifferenceInPayOrder,
    queryExternalService,
  } = props;

  const [form] = Form.useForm();

  const columns = [
    {
      title: 'Tipo de pago',
      dataIndex: 'type',
      key: 'type',
      render: (text: PaymentType) =>
        text.charAt(0).toUpperCase() + text.slice(1),
    },
    {
      title: 'Debe/Haber',
      dataIndex: 'DebeHaber',
      key: 'DebeHaber',
      render: (text: number) =>
        text === DebeHaber.DEBE ? (
          <Tag color="red">Debe</Tag>
        ) : (
          <Tag color="green">Haber</Tag>
        ),
    },
    {
      title: 'Cuenta',
      dataIndex: 'CuentaCodigo',
      key: 'CuentaCodigo',
      render: (text: string) =>
        accounts.find((account) => account.CODIGO === text)?.NOMBRE || text,
    },
    {
      title: 'Moneda',
      dataIndex: 'MonedaCodigo',
      key: 'MonedaCodigo',
      render: (text: string) =>
        text === Moneda.PESOS
          ? 'Pesos'
          : text === Moneda.DOLARES
          ? 'Dolares'
          : text,
    },
    {
      title: 'Importe',
      dataIndex: 'ImporteMonTransaccion',
      key: 'ImporteMonTransaccion',
      render: (amount: number, record: PayOrderPayments) => {
        const value = record.DebeHaber === DebeHaber.DEBE ? -amount : amount;
        return formatValueOnLoad(value);
      },
    },
    {
      title: 'Acciones',
      key: 'actions',
      align: 'right' as const,
      width: 'auto',
      render: (_: any, record: PayOrderPayments, index: number) => (
        <div>
          <span
            translate="no"
            className="material-symbols-outlined pointer concept-action-icon"
            onClick={() => openEditModal(record, index)}
          >
            edit
          </span>
          <span
            translate="no"
            className="material-symbols-outlined pointer concept-action-icon"
            onClick={() => handleDeletePayment(index)}
          >
            delete
          </span>
        </div>
      ),
    },
  ];

  const [addPaymentModal, setAddPaymentModal] = useState(false);
  const [editingPayment, setEditingPayment] =
    useState<PayOrderPayments | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [differenceInCents, setDifferenceInCents] = useState(0);
  const [totalPaymentCents, setTotalPaymentCents] = useState(0);

  const openEditModal = (payment: PayOrderPayments, index: number) => {
    setEditingPayment(payment);
    setEditingIndex(index);
    form.setFieldsValue({
      ...payment,
      ...(payment.type === PaymentType.BANCO && {
        OperacionBancariaCodigo: (payment as BankPaymentMethod)
          .OperacionBancariaCodigo,
        NumeroDocumentoFisico: (payment as BankPaymentMethod)
          .NumeroDocumentoFisico,
      }),
    });
    setAddPaymentModal(true);
  };

  const handleAddPayment = (paymentMethod: PayOrderPayments) => {
    setExtraData({
      ...extraData,
      payOrderPayments: [...extraData.payOrderPayments!, paymentMethod],
    });
    setAddPaymentModal(false);
  };

  const handleEditPayment = (paymentMethod: PayOrderPayments) => {
    if (editingIndex !== null) {
      const updatedPaymentMethods = [...extraData.payOrderPayments!];
      updatedPaymentMethods[editingIndex] = paymentMethod;
      setExtraData({
        ...extraData,
        payOrderPayments: updatedPaymentMethods,
      });
    }
    setEditingPayment(null);
    setEditingIndex(null);
    setAddPaymentModal(false);
  };

  const handleDeletePayment = (index: number) => {
    const updatedPaymentMethods = [...extraData.payOrderPayments!];
    updatedPaymentMethods.splice(index, 1);
    setExtraData({
      ...extraData,
      payOrderPayments: updatedPaymentMethods,
    });
  };

  const getTotalPayments = () => {
    return extraData.payOrderPayments!.reduce((total, method) => {
      const amount =
        method.DebeHaber === 1
          ? -method.ImporteMonTransaccion
          : method.ImporteMonTransaccion;
      return total + amount;
    }, 0);
  };

  useEffect(() => {
    const receiptTotal = receiptHeader?.receipt_total || 0;
    const receiptTotalCents = Math.round(receiptTotal * 100);
    const paymentInCents = Math.round(getTotalPayments() * 100);
    const diffInCents = receiptTotalCents - paymentInCents;
    setDifferenceInCents(diffInCents);
    setTotalPaymentCents(paymentInCents);
    setHasDifferenceInPayOrder(diffInCents !== 0);
  }, [extraData.payOrderPayments]);

  return (
    <>
      <div className="payment__list">
        <Table<PayOrderPayments>
          dataSource={extraData?.payOrderPayments}
          columns={columns}
          rowKey={(record, index) => `${index}-${record.type}`}
          pagination={false}
          size="small"
          className="payment-table"
          footer={() => (
            <div className="payment-totals__footer">
              {differenceInCents !== 0 && (
                <Alert
                  message="El movimiento contable no balancea, si desea continuar se creará una entrada de tipo “Otro” para solventar la diferencia, dicho movimiento usará la cuenta definida en el formulario de integración."
                  type="info"
                  showIcon
                  style={{ borderRadius: 4, fontSize: 11 }}
                />
              )}
              <div className="payment-totals">
                <div className="payment-totals__item">
                  <span className="payment-totals__label">
                    Total a cancelar:
                  </span>
                  <span className="payment-totals__value">
                    ${formatValueOnLoad(receiptHeader?.receipt_total!)}
                  </span>
                </div>
                <div className="payment-totals__item">
                  <span className="payment-totals__label">
                    Total cancelado:
                  </span>
                  <span className="payment-totals__value">
                    ${formatValueOnLoad(totalPaymentCents / 100)}
                  </span>
                </div>
                <div className="payment-totals__item">
                  <span className="payment-totals__label">Diferencia:</span>
                  <span
                    className={`payment-totals__value ${
                      differenceInCents === 0
                        ? 'payment-totals__value--match'
                        : 'payment-totals__value--mismatch'
                    }`}
                  >
                    ${formatValueOnLoad(differenceInCents / 100)}
                  </span>
                </div>
              </div>
            </div>
          )}
          locale={{
            emptyText: 'Aún no se han agregado formas de pago',
          }}
          summary={() => (
            <Table.Summary>
              <Table.Summary.Row>
                <Table.Summary.Cell colSpan={columns.length} index={0}>
                  <Button
                    className="distribution-block__action-button"
                    type="link"
                    onClick={() => {
                      form.resetFields();
                      setEditingPayment(null);
                      setEditingIndex(null);
                      setAddPaymentModal(true);
                    }}
                  >
                    <PlusCircleOutlined className="distribution-block__action-icon" />
                    <span className="distribution-block__action-label">
                      Agregar método de pago
                    </span>
                  </Button>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />
      </div>
      <Modal
        open={addPaymentModal}
        onCancel={() => {
          setAddPaymentModal(false);
          setEditingPayment(null);
          setEditingIndex(null);
        }}
        title={
          editingPayment ? 'Editar forma de pago' : 'Agregar forma de pago'
        }
        width={400}
        footer={null}
        style={{ overflow: 'hidden' }}
      >
        <AddPaymentForm
          onAddPayment={handleAddPayment}
          onEditPayment={handleEditPayment}
          editingPayment={editingPayment}
          accounts={accounts}
          bankOperations={bankOperations}
          form={form}
          queryExternalService={queryExternalService}
        />
      </Modal>
    </>
  );
};

interface AddPaymentFormProps {
  onAddPayment: (paymentMethod: PayOrderPayments) => void;
  onEditPayment: (paymentMethod: PayOrderPayments) => void;
  editingPayment: PayOrderPayments | null;
  accounts: AccountValue[];
  bankOperations: OperacionesBancarias[];
  form: FormInstance;
  queryExternalService: FrontendExtensionProps['queryExternalService'];
}

interface AccountDistributionBlockProps {
  loading: boolean;
  error: string | null;
  accountDimensions: FinnegansAccountDimension[];
  distributionsByDimension: Record<string, Distribution[]>;
  dimensionValuesByDimensionCode: Record<string, DimensionValue[]>;
  selectedDistributions: Record<string, string>;
  manualValuesByDimension: Record<string, ExtensionDimensionValue[]>;
  onChangeDistribution: (
    dimensionCode: string,
    distributionCode: string,
  ) => void;
  onAddManualValue: (dimensionCode: string) => void;
  onRemoveManualValue: (dimensionCode: string, index: number) => void;
  onUpdateManualValue: (
    dimensionCode: string,
    index: number,
    field: keyof ExtensionDimensionValue,
    value: string | number,
  ) => void;
  cuentaCodigo: string | undefined;
}

const AccountDistributionBlock = (props: AccountDistributionBlockProps) => {
  const {
    loading,
    error,
    accountDimensions,
    distributionsByDimension,
    dimensionValuesByDimensionCode,
    selectedDistributions,
    manualValuesByDimension,
    onChangeDistribution,
    onAddManualValue,
    onRemoveManualValue,
    onUpdateManualValue,
    cuentaCodigo,
  } = props;

  if (!cuentaCodigo) return null;

  if (loading) {
    return (
      <div style={{ marginBottom: 16 }}>
        <Spin size="small" /> Cargando dimensiones de la cuenta...
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        type="error"
        message={error}
        showIcon
        style={{ marginBottom: 16 }}
      />
    );
  }

  if (!accountDimensions || accountDimensions.length === 0) {
    return (
      <div
        style={{ marginBottom: 16, color: 'rgba(0,0,0,0.45)', fontSize: 12 }}
      >
        Esta cuenta no posee dimensiones configuradas.
      </div>
    );
  }

  return (
    <div style={{ marginBottom: 16 }}>
      <label className="extension-label">Distribución por dimensión</label>
      {accountDimensions.map((dim) => {
        const distributions =
          distributionsByDimension[dim.DimensionCodigo] || [];
        const availableValues =
          dimensionValuesByDimensionCode[dim.DimensionCodigo] || [];
        const value = selectedDistributions[dim.DimensionCodigo];
        const isManual = value === DISTRIBUTION_MANUAL;
        const manualValues = manualValuesByDimension[dim.DimensionCodigo] || [];
        return (
          <Form.Item
            key={dim.DimensionCodigo}
            label={dim.DimensionCodigo}
            style={{ marginBottom: 8 }}
          >
            <Select
              placeholder="Seleccione una distribución"
              showSearch
              optionFilterProp="label"
              value={value}
              onChange={(v) => onChangeDistribution(dim.DimensionCodigo, v)}
              options={[
                { value: DISTRIBUTION_MANUAL, label: 'Distribución manual' },
                ...distributions.map((d) => ({
                  value: d.dimensionDistributionCode,
                  label: d.dimensionDistributionName,
                })),
              ]}
            />
            {isManual && (
              <div style={{ marginTop: 8 }}>
                {manualValues.map((manualValue, idx) => (
                  <div
                    key={`${dim.DimensionCodigo}-${idx}`}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr 30px',
                      gap: 8,
                      marginBottom: 4,
                    }}
                  >
                    <Select
                      showSearch
                      placeholder="Buscar valor"
                      optionFilterProp="label"
                      value={manualValue.code || undefined}
                      onChange={(v) =>
                        onUpdateManualValue(
                          dim.DimensionCodigo,
                          idx,
                          'code',
                          v,
                        )
                      }
                      options={availableValues
                        .filter(
                          (item) =>
                            !manualValues.some(
                              (val) => val.code === item.CODIGO,
                            ) || item.CODIGO === manualValue.code,
                        )
                        .map((item) => ({
                          value: item.CODIGO,
                          label: item.NOMBRE,
                        }))}
                    />
                    <Input
                      type="number"
                      value={manualValue.percentage}
                      onChange={(e) =>
                        onUpdateManualValue(
                          dim.DimensionCodigo,
                          idx,
                          'percentage',
                          Number(e.target.value),
                        )
                      }
                    />
                    <Button
                      type="link"
                      onClick={() =>
                        onRemoveManualValue(dim.DimensionCodigo, idx)
                      }
                    >
                      <MinusCircleOutlined />
                    </Button>
                  </div>
                ))}
                <Button
                  type="link"
                  onClick={() => onAddManualValue(dim.DimensionCodigo)}
                >
                  <PlusCircleOutlined /> Agregar nuevo valor
                </Button>
              </div>
            )}
            {!isManual && distributions.length === 0 && !value && (
              <div style={{ color: 'rgba(0,0,0,0.45)', fontSize: 12 }}>
                Esta dimensión no posee distribuciones predefinidas. Podés
                usar la distribución manual.
              </div>
            )}
          </Form.Item>
        );
      })}
    </div>
  );
};

const AddPaymentForm = (props: AddPaymentFormProps) => {
  const {
    onAddPayment,
    onEditPayment,
    editingPayment,
    accounts,
    bankOperations,
    form,
    queryExternalService,
  } = props;
  const [paymentType, setPaymentType] = useState<PaymentType>(
    editingPayment ? editingPayment.type : PaymentType.EFECTIVO,
  );

  const cuentaCodigo = Form.useWatch('CuentaCodigo', form);
  const [accountDimensions, setAccountDimensions] = useState<
    FinnegansAccountDimension[]
  >([]);
  const [distributionsByDimension, setDistributionsByDimension] = useState<
    Record<string, Distribution[]>
  >({});
  const [dimensionValuesByDimensionCode, setDimensionValuesByDimensionCode] =
    useState<Record<string, DimensionValue[]>>({});
  const [selectedDistributions, setSelectedDistributions] = useState<
    Record<string, string>
  >({});
  const [manualValuesByDimension, setManualValuesByDimension] = useState<
    Record<string, ExtensionDimensionValue[]>
  >({});
  const [loadingDimensions, setLoadingDimensions] = useState(false);
  const [dimensionsError, setDimensionsError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchAccountData = async () => {
      if (!cuentaCodigo) {
        setAccountDimensions([]);
        setDistributionsByDimension({});
        setDimensionValuesByDimensionCode({});
        setSelectedDistributions({});
        setManualValuesByDimension({});
        setDimensionsError(null);
        return;
      }
      setLoadingDimensions(true);
      setDimensionsError(null);
      try {
        const accountData = await queryExternalService<
          FinnegansAccountResponse,
          AvailableExternalQueries.GET_ACCOUNT
        >({
          query: AvailableExternalQueries.GET_ACCOUNT,
          inputData: { accountCode: cuentaCodigo },
        });
        if (cancelled) return;
        const dims = accountData?.CuentaDimension || [];
        setAccountDimensions(dims);

        if (dims.length === 0) {
          setDistributionsByDimension({});
          setDimensionValuesByDimensionCode({});
          setSelectedDistributions({});
          setManualValuesByDimension({});
          return;
        }

        const dimensionCodes = Array.from(
          new Set(dims.map((d) => d.DimensionCodigo)),
        );

        const [dimensionValuesResp, distributionsResp] = await Promise.all([
          queryExternalService<
            Record<string, DimensionValue[]>,
            AvailableExternalQueries.GET_DIMENSION_VALUES
          >({
            query: AvailableExternalQueries.GET_DIMENSION_VALUES,
            inputData: { dimensionCodes },
          }),
          queryExternalService<
            Record<string, DistributionResponse[]>,
            AvailableExternalQueries.GET_DISTRIBUTIONS
          >({
            query: AvailableExternalQueries.GET_DISTRIBUTIONS,
            inputData: { dimensionCodes },
          }),
        ]);
        if (cancelled) return;

        const parsed: Record<string, Distribution[]> = {};
        Object.entries(distributionsResp || {}).forEach(
          ([dimCode, distributions]) => {
            const dimValues = dimensionValuesResp?.[dimCode] || [];
            parsed[dimCode] = (distributions || []).map((d) => ({
              dimensionDistributionCode: d.dimensionDistribucionCodigo,
              dimensionDistributionName: d.dimensionDistribucionNombre,
              values: (d.valores || []).map((v) => {
                const dimValue = dimValues.find(
                  (dv) => dv.ID === v.dimensionDistribucionValorId,
                );
                return {
                  code: dimValue?.CODIGO || '',
                  name: dimValue?.NOMBRE || '',
                  percentage: v.porcentaje,
                };
              }),
            }));
          },
        );
        setDistributionsByDimension(parsed);
        setDimensionValuesByDimensionCode(dimensionValuesResp || {});

        const preselected: Record<string, string> = {};
        const preselectedManualValues: Record<
          string,
          ExtensionDimensionValue[]
        > = {};
        const editingDims = (editingPayment as any)?.DimensionDistribucion as
          | FinnegansDimension[]
          | undefined;
        dims.forEach((d) => {
          const editingDim = editingDims?.find(
            (e) => e.dimensionCodigo === d.DimensionCodigo,
          );
          const editingDistribucionCodigo = editingDim?.distribucionCodigo;
          const isKnownDistribution = (parsed[d.DimensionCodigo] || []).some(
            (dist) =>
              dist.dimensionDistributionCode === editingDistribucionCodigo,
          );

          if (
            editingDim &&
            (!editingDistribucionCodigo || !isKnownDistribution) &&
            (editingDim.distribucionItems || []).length > 0
          ) {
            preselected[d.DimensionCodigo] = DISTRIBUTION_MANUAL;
            preselectedManualValues[d.DimensionCodigo] = (
              editingDim.distribucionItems || []
            ).map((item) => ({
              code: item.codigo,
              percentage: item.porcentaje,
            }));
            return;
          }

          const fromAccount = d.DimensionDistribucionCodigo || undefined;
          const value = editingDistribucionCodigo || fromAccount;
          if (value) preselected[d.DimensionCodigo] = value;
        });
        setSelectedDistributions(preselected);
        setManualValuesByDimension(preselectedManualValues);
      } catch (err) {
        if (cancelled) return;
        setDimensionsError(
          'No se pudieron cargar las dimensiones de la cuenta.',
        );
        setAccountDimensions([]);
        setDistributionsByDimension({});
        setDimensionValuesByDimensionCode({});
        setSelectedDistributions({});
        setManualValuesByDimension({});
      } finally {
        if (!cancelled) setLoadingDimensions(false);
      }
    };
    fetchAccountData();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cuentaCodigo]);

  const buildDimensionDistribucion = (): FinnegansDimension[] => {
    return accountDimensions
      .map((d) => {
        const distribucionCodigo = selectedDistributions[d.DimensionCodigo];
        if (!distribucionCodigo) return null;

        if (distribucionCodigo === DISTRIBUTION_MANUAL) {
          const manualValues =
            manualValuesByDimension[d.DimensionCodigo] || [];
          const distribucionItems = manualValues
            .filter((v) => v.code)
            .map((v) => ({
              codigo: v.code,
              porcentaje: v.percentage,
            }));
          if (distribucionItems.length === 0) return null;
          return {
            tipoCalculo: '2',
            dimensionCodigo: d.DimensionCodigo,
            distribucionItems,
          } as FinnegansDimension;
        }

        const distribution = (
          distributionsByDimension[d.DimensionCodigo] || []
        ).find((dist) => dist.dimensionDistributionCode === distribucionCodigo);
        const distribucionItems = (distribution?.values || [])
          .filter((v) => v.code)
          .map((v) => ({
            codigo: v.code,
            porcentaje: v.percentage,
          }));
        return {
          tipoCalculo: '2',
          dimensionCodigo: d.DimensionCodigo,
          distribucionCodigo,
          distribucionItems,
        } as FinnegansDimension;
      })
      .filter((d): d is FinnegansDimension => d !== null);
  };

  const handleSubmit = (values: any) => {
    const dimensionDistribucion = buildDimensionDistribucion();
    const basePayment = {
      DebeHaber: values.DebeHaber || DebeHaber.HABER,
      ImporteMonTransaccion: values.ImporteMonTransaccion,
      MonedaCodigo: values.MonedaCodigo,
      CuentaCodigo: values.CuentaCodigo,
    };

    let paymentMethod: PayOrderPayments;

    switch (values.type) {
      case PaymentType.EFECTIVO:
        paymentMethod = {
          ...basePayment,
          DebeHaber: DebeHaber.HABER,
          type: PaymentType.EFECTIVO,
          DimensionDistribucion: dimensionDistribucion,
        } as CashPaymentMethod;
        break;
      case PaymentType.BANCO:
        paymentMethod = {
          ...basePayment,
          DebeHaber: DebeHaber.HABER,
          type: PaymentType.BANCO,
          OperacionBancariaCodigo: values.OperacionBancariaCodigo,
          NumeroDocumentoFisico: values.NumeroDocumentoFisico,
          DimensionDistribucion: dimensionDistribucion,
        } as BankPaymentMethod;
        break;
      case PaymentType.OTRO:
      default:
        paymentMethod = {
          ...basePayment,
          type: PaymentType.OTRO,
          DimensionDistribucion: dimensionDistribucion,
        } as OtherPaymentMethod;
        break;
    }

    if (editingPayment) {
      onEditPayment(paymentMethod);
    } else {
      onAddPayment(paymentMethod);
    }
  };

  const handleAddManualValue = (dimensionCode: string) => {
    setManualValuesByDimension((prev) => ({
      ...prev,
      [dimensionCode]: [...(prev[dimensionCode] || []), { code: '', percentage: 0 }],
    }));
  };

  const handleRemoveManualValue = (dimensionCode: string, index: number) => {
    setManualValuesByDimension((prev) => ({
      ...prev,
      [dimensionCode]: (prev[dimensionCode] || []).filter(
        (_value, idx) => idx !== index,
      ),
    }));
  };

  const handleUpdateManualValue = (
    dimensionCode: string,
    index: number,
    field: keyof ExtensionDimensionValue,
    value: string | number,
  ) => {
    setManualValuesByDimension((prev) => ({
      ...prev,
      [dimensionCode]: (prev[dimensionCode] || []).map((item, idx) =>
        idx === index ? { ...item, [field]: value } : item,
      ),
    }));
  };

  const handleOperacionChange = (codigoOperacion: string) => {
    const selectedOperacion = bankOperations.find(
      (op) => op.CODIGO === codigoOperacion,
    );

    if (
      selectedOperacion?.CUENTACODIGODEFAULT &&
      accounts.some(
        (acc) =>
          acc.CODIGO === selectedOperacion.CUENTACODIGODEFAULT &&
          acc.USO === 'Banco',
      )
    ) {
      form.setFieldsValue({
        CuentaCodigo: selectedOperacion.CUENTACODIGODEFAULT,
      });
    }
  };

  return (
    <div className="add-payment-form">
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          type: paymentType,
          DebeHaber: DebeHaber.HABER,
          ImporteMonTransaccion: 0,
        }}
        style={{ width: 350 }}
      >
        <Form.Item
          name="type"
          label="Tipo de pago"
          rules={[{ required: true, message: 'Seleccione un tipo de pago' }]}
        >
          <Select
            placeholder="Selecciona una forma de pago"
            options={Object.values(PaymentType).map((value) => ({
              value,
              label: value.charAt(0).toUpperCase() + value.slice(1),
            }))}
            onChange={(value) => setPaymentType(value)}
            defaultValue={paymentType}
          />
        </Form.Item>

        {paymentType === PaymentType.OTRO && (
          <Form.Item
            name="DebeHaber"
            label="Tipo de movimiento"
            rules={[{ required: true, message: 'Seleccione Debe o Haber' }]}
            initialValue={DebeHaber.HABER}
          >
            <Select
              options={[
                { value: DebeHaber.DEBE, label: 'Debe' },
                { value: DebeHaber.HABER, label: 'Haber' },
              ]}
            />
          </Form.Item>
        )}

        {paymentType === PaymentType.BANCO && (
          <>
            <Form.Item
              name="OperacionBancariaCodigo"
              label="Operación bancaria"
              rules={[
                {
                  required: true,
                  message: 'Ingrese el código de operación bancaria',
                },
              ]}
            >
              <Select
                placeholder="Seleccione una operación bancaria"
                showSearch
                optionFilterProp="label"
                onChange={handleOperacionChange}
                options={bankOperations.map((operation) => ({
                  value: operation.CODIGO,
                  label: `${operation.CODIGO} - ${operation.NOMBRE}`,
                }))}
              />
            </Form.Item>

            <Form.Item
              name="NumeroDocumentoFisico"
              label="Número de documento físico"
              rules={[
                {
                  required: true,
                  message: 'Ingrese el número de documento físico',
                },
              ]}
            >
              <Input />
            </Form.Item>
          </>
        )}

        <Form.Item
          name="CuentaCodigo"
          label="Cuenta"
          rules={[{ required: true, message: 'Seleccione una cuenta' }]}
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.OperacionBancariaCodigo !==
            currentValues.OperacionBancariaCodigo
          }
        >
          <Select
            placeholder="Seleccione una cuenta"
            showSearch
            optionFilterProp="label"
            options={accounts
              .filter((account) => {
                if (!paymentType) return false;

                const accountTypeMap = {
                  [PaymentType.EFECTIVO]: 'Efectivo',
                  [PaymentType.BANCO]: 'Banco',
                  [PaymentType.OTRO]: 'Otros',
                };

                return account.USO === accountTypeMap[paymentType];
              })
              .map((account) => ({
                value: account.CODIGO,
                label: `${account.CODIGO} - ${account.NOMBRE}`,
              }))}
          />
        </Form.Item>

        <AccountDistributionBlock
          loading={loadingDimensions}
          error={dimensionsError}
          accountDimensions={accountDimensions}
          distributionsByDimension={distributionsByDimension}
          dimensionValuesByDimensionCode={dimensionValuesByDimensionCode}
          selectedDistributions={selectedDistributions}
          manualValuesByDimension={manualValuesByDimension}
          onChangeDistribution={(dimensionCode, distributionCode) => {
            setSelectedDistributions((prev) => ({
              ...prev,
              [dimensionCode]: distributionCode,
            }));
            if (distributionCode === DISTRIBUTION_MANUAL) {
              setManualValuesByDimension((prev) => ({
                ...prev,
                [dimensionCode]: prev[dimensionCode] || [],
              }));
            }
          }}
          onAddManualValue={handleAddManualValue}
          onRemoveManualValue={handleRemoveManualValue}
          onUpdateManualValue={handleUpdateManualValue}
          cuentaCodigo={cuentaCodigo}
        />

        <Form.Item
          name="MonedaCodigo"
          label="Moneda"
          initialValue={Moneda.PESOS}
        >
          <Select
            options={[
              { value: Moneda.PESOS, label: 'Pesos' },
              { value: Moneda.DOLARES, label: 'Dolares' },
            ]}
          />
        </Form.Item>

        <Form.Item
          name="ImporteMonTransaccion"
          label="Importe"
          rules={[{ required: true, message: 'Ingrese el importe' }]}
        >
          <InputNumber decimalSeparator="," />
        </Form.Item>

        <Form.Item>
          <div className="add-payment-form__actions">
            <Button onClick={() => form.resetFields()}>Limpiar</Button>
            <Button type="primary" htmlType="submit">
              {editingPayment ? 'Guardar cambios' : 'Agregar'}
            </Button>
          </div>
        </Form.Item>
      </Form>
    </div>
  );
};
